import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Switch } from '../Switch/Switch';
import { Button } from '../Button/Button';
import { AlertDialog } from '../AlertDialog/AlertDialog';
import { MasterCrudTable } from './MasterCrudTable';
import { MasterCrudForm } from './MasterCrudForm';
import { MasterCrudToolbar } from './MasterCrudToolbar';
import { MasterCRUDCard } from './MasterCRUDCard';
import { MasterCrudAdvancedSearch } from './MasterCrudAdvancedSearch';
import { CompanySelector } from './CompanySelector';
import { LinkModal } from './LinkModal';
import { MatchModal } from '../MatchModal';
import { Toast } from '../Toast';
import { Badge } from '../Badge/Badge';
import { Info } from '../Info/Info';
import { Dropdown } from '../Dropdown/Dropdown';
import noDataImage from '../../assets/nodata.svg';
import type { ToastColor } from '../Toast/Toast.types';
import type {
  MasterCrudProps,
  ViewState,
  GetAllParams,
  ToolbarViewType,
  MasterCrudAction,
  AdvancedFilter,
  MasterCrudCompany,
  MatchResult,
  MasterCrudField,
  MasterCrudOperationType,
  BulkUpdateResult,
  FileManagerConfig,
  CrudService,
  MasterCrudDetailConfig,
} from './MasterCrud.types';
import { MasterCrudDetailSection } from './MasterCrudDetailSection';
import { MasterPatternCrudAdapter } from './MasterPatternAdapter';
import type { GalleryResource } from '../FileUploaderGallery/FileUploaderGallery.types';
import { MasterCrudBulkDynamicEditor } from './MasterCrudBulkDynamicEditor';
import { PlusIcon, CloseIcon as XMarkIcon, PencilIcon, TrashIcon, BackIcon, Bars3Icon, ChevronDownIcon, GlobeAltIcon, LinkIcon, MinusCircleIcon, CheckCircleIcon, ClockIcon, EllipsisVerticalIcon, NoSymbolIcon } from './icons';
import { AuditHistoryPanel } from '../../audit/components/AuditHistoryPanel';

import { useMasterCrudTranslation } from './useMasterCrudTranslation';

// ===== TRADUCCIONES INTERNAS (FALLBACK) =====
const internalTranslations: Record<string, any> = {
  es: {
    save: 'Guardar',
    cancel: 'Cancelar',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    activate: 'Activar',
    deactivate: 'Desactivar',
    search: 'Buscar',
    searchBy: 'Buscar por',
    filterBy: 'Filtrar por',
    sortBy: 'Ordenar por',
    all: 'Todos',
    noSort: 'Sin orden',
    empty: 'No hay registros disponibles',
    confirmDeleteTitle: '¿Estás seguro de eliminar este registro?',
    confirmDeleteDesc: 'Esta acción no se puede deshacer.',
    confirmDeleteText: 'Eliminar',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    errorTitle: 'Error',
    incompleteTitle: 'Información incompleta',
    incompleteMessage: 'Para guardar, completa los siguientes campos obligatorios',
    successTitle: 'Éxito',
    defaultError: 'Ha ocurrido un error inesperado',
    noPermission: 'No tienes permiso de lectura para ver estos registros',
    loading: 'Cargando...',
    preview: 'Vista Previa',
    advancedSearch: 'Búsqueda Avanzada',
    globalValues: 'Valores Globales',
    useGlobalData: 'Usar datos globales',
    linkCompanies: 'Habilitar compañías',
    createNew: 'Crear nuevo registro',
    revertToGlobal: 'Restaurar a valores globales',
    showGlobalValues: 'Mostrar valores globales',
    confirmGlobalResetTitle: '¿Restaurar campos?',
    confirmGlobalResetDesc: 'Se perderán los cambios locales y se usarán los valores maestros.',
    confirmCreateTitle: '¿Crear nuevo registro?',
    confirmCreateDesc: 'Se perderán los cambios no guardados.',
    isRequired: 'es requerido',
    back: 'Volver',
    saving: 'Guardando...',
    successCreate: 'Registro creado correctamente',
    successUpdate: 'Registro actualizado correctamente',
    successDelete: 'Registro eliminado correctamente',
    successDeactivate: 'Registro desactivado correctamente',
    successActivate: 'Registro activado correctamente',
    successUnlink: 'Registro deshabilitado correctamente',
    successLink: 'Registro habilitado correctamente',
    successRevert: 'Campos restaurados a valores globales',
    confirmDeactivateTitle: '¿Estás seguro de desactivar este registro?',
    confirmDeactivateDesc: 'El registro dejará de estar disponible.',
    confirmDeactivateCompanyDesc: 'El registro dejará de estar disponible en esta compañía.',
    confirmUnlinkTitle: '¿Estás seguro de deshabilitar este registro?',
    confirmUnlinkDesc: 'El registro ya no estará habilitado en esta sucursal.',
    confirmActivateTitle: '¿Estás seguro de activar este registro?',
    confirmActivateDesc: 'El registro volverá a estar disponible.',
    confirmActivateCompanyDesc: 'El registro volverá a estar disponible en esta compañía.',
    confirmLinkTitle: '¿Estás seguro de habilitar este registro?',
    confirmLinkDesc: 'El registro quedará habilitado en esta sucursal.',
    errorDetailLoad: 'Error al cargar el detalle del registro',
    errorDataLoad: 'Error al cargar los datos del registro',
    errorRecordNotFound: 'No se encontró el registro seleccionado',
    errorLinkDynamic: 'Error al habilitar dinámicamente',
    successLinkDynamic: 'Registro habilitado y cargado',
    previous: 'Anterior',
    next: 'Siguiente',
    perPage: 'por página',
    records: 'registros',
    goToPage: 'Página',
    // Alineación con el prototipo (listado + detalle)
    moreOptions: 'Más opciones',
    moreActions: 'Más acciones',
    auditChanges: 'Auditoría de Cambios',
    export: 'Exportar',
    displayMode: 'Modo de visualización',
    listView: 'Listado',
    cardView: 'Tarjetas',
    recordsToShow: 'Registros a mostrar',
    viewAll: 'Ver todos los registros',
    viewCompany: 'Ver registros de la compañía seleccionada',
    viewCompanyOnly: 'Ver solo registros de la compañía seleccionada',
    // Modal de habilitación por compañía (LinkModal)
    searchCompanyPlaceholder: 'Buscar compañía…',
    noCompaniesFound: 'No se encontraron compañías',
    // Conector lógico de búsqueda avanzada
    joinConditionsWith: 'Unir condiciones con',
    connectorAnd: 'Y',
    connectorOr: 'O',
    connectorAndHint: 'Se deben cumplir todas las condiciones',
    connectorOrHint: 'Basta con que se cumpla una condición',
    // Editor de edición masiva de campos dinámicos (MasterCrudBulkDynamicEditor)
    close: 'Cerrar',
    bulkEditTitle: 'Editar campos dinámicos',
    bulkLoadingFields: 'Cargando campos dinámicos...',
    bulkLoadError: 'Error al cargar las entidades dinámicas',
    bulkEditingIntroPrefix: 'Se aplicarán cambios a',
    bulkEditingIntroSuffix: 'registros seleccionados. Llena solo los campos que deseas modificar; los campos vacíos no se alterarán.',
    bulkNoDynamicEntities: 'No hay entidades dinámicas configuradas para este módulo.',
    bulkMultiRecordNote: 'Solo se actualizará la fila principal de cada registro',
    bulkReadonlyEntity: 'Esta entidad es de solo lectura',
    bulkChangesSummary: 'Resumen de cambios',
    bulkWillApply: 'Se aplicarán',
    bulkFieldsLabel: 'campo(s)',
    bulkToLabel: 'a',
    bulkRecordsLabel: 'registro(s)',
    bulkProcessing: 'Procesando registros...',
    bulkSucceededLabel: 'exitosos',
    bulkFailedLabel: 'fallidos',
    bulkOperationCompleted: 'Operación completada',
    bulkOfLabel: 'de',
    bulkRecordsPlural: 'registros',
    bulkRecordsWithError: 'Registros con error',
    recordIdCsv: 'ID del Registro',
    reasonCsv: 'Motivo',
    bulkPreview: 'Previsualizar',
    bulkApplyTo: 'Aplicar a',
    bulkCancelOperation: 'Cancelar operación',
    bulkExportErrorsCsv: 'Exportar errores CSV',
    bulkConfirmTitle: 'Confirmar modificación masiva',
    bulkConfirmLine1Prefix: 'Vas a modificar',
    bulkConfirmLine1Suffix: 'registro(s). Esta acción no se puede deshacer.',
    bulkConfirmTypePrompt: 'Para confirmar, escribe',
    bulkConfirmAriaLabel: 'Confirmación de modificación masiva',
    showAdvancedSearch: 'Mostrar búsqueda avanzada',
    hideAdvancedSearch: 'Ocultar búsqueda avanzada',
    filter: 'Filtrar',
    hideFilter: 'Ocultar filtro',
    editing: 'Edición',
    creating: 'Creación',
    companyStatus: 'Habilitación en la compañía seleccionada',
    enabledInCompany: 'Habilitado en la compañía seleccionada',
    notEnabledInCompany: 'No habilitado en la compañía seleccionada',
    enabled: 'Habilitado',
    notEnabled: 'No habilitado',
    masterRecord: 'Registro maestro',
    unsavedTitle: 'Tienes cambios sin guardar',
    unsavedDesc: 'Si sales ahora perderás los siguientes cambios. ¿Deseas guardarlos antes de volver al listado?',
    modifiedFields: 'Campos modificados',
    emptyValue: 'vacío',
    keepEditing: 'Seguir editando',
    discardExit: 'Descartar y salir',
    saveExit: 'Guardar y salir',
  },
  en: {
    save: 'Save',
    cancel: 'Cancel',
    cancelText: 'Cancel',
    confirmText: 'Confirm',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    activate: 'Activate',
    deactivate: 'Deactivate',
    search: 'Search',
    searchBy: 'Search by',
    filterBy: 'Filter by',
    sortBy: 'Sort by',
    all: 'All',
    noSort: 'No sort',
    empty: 'No records available',
    confirmDeleteTitle: 'Are you sure you want to delete this record?',
    confirmDeleteDesc: 'This action cannot be undone.',
    confirmDeleteText: 'Delete',
    confirmDeactivateTitle: 'Are you sure you want to deactivate this record?',
    confirmDeactivateDesc: 'The record will no longer be available.',
    confirmActivateTitle: 'Are you sure you want to activate this record?',
    confirmActivateDesc: 'The record will be available again.',
    confirmDeactivateCompanyDesc: 'The record will no longer be available in this company.',
    confirmActivateCompanyDesc: 'The record will be available again in this company.',
    errorTitle: 'Error',
    incompleteTitle: 'Incomplete information',
    incompleteMessage: 'To save, complete the following required fields',
    successTitle: 'Success',
    defaultError: 'An unexpected error occurred',
    noPermission: 'You do not have read permission to view these records',
    loading: 'Loading...',
    preview: 'Preview',
    advancedSearch: 'Advanced Search',
    globalValues: 'Global Values',
    useGlobalData: 'Use global data',
    linkCompanies: 'Enable companies',
    createNew: 'Create new record',
    revertToGlobal: 'Restore to global values',
    showGlobalValues: 'Show global values',
    confirmGlobalResetTitle: 'Restore fields?',
    confirmGlobalResetDesc: 'Local changes will be lost and master values will be used.',
    confirmCreateTitle: 'Create new record?',
    confirmCreateDesc: 'Unsaved changes will be lost.',
    isRequired: 'is required',
    back: 'Back',
    saving: 'Saving...',
    successCreate: 'Record created successfully',
    successUpdate: 'Record updated successfully',
    successDelete: 'Record deleted successfully',
    successDeactivate: 'Record deactivated successfully',
    successActivate: 'Record activated successfully',
    successRevert: 'Fields restored to master values',
    errorDetailLoad: 'Error loading record details',
    errorDataLoad: 'Error loading record data',
    errorRecordNotFound: 'Selected record not found',
    errorLinkDynamic: 'Error enabling dynamically',
    successLinkDynamic: 'Record enabled and loaded',
    previous: 'Previous',
    next: 'Next',
    perPage: 'per page',
    records: 'records',
    goToPage: 'Page',
    moreOptions: 'More options',
    moreActions: 'More actions',
    auditChanges: 'Change audit',
    export: 'Export',
    displayMode: 'Display mode',
    listView: 'List',
    cardView: 'Cards',
    recordsToShow: 'Records to show',
    viewAll: 'View all records',
    viewCompany: 'View records for the selected company',
    viewCompanyOnly: 'Show only records for the selected company',
    // Company enablement modal (LinkModal)
    searchCompanyPlaceholder: 'Search company…',
    noCompaniesFound: 'No companies found',
    // Advanced search logical connector
    joinConditionsWith: 'Join conditions with',
    connectorAnd: 'AND',
    connectorOr: 'OR',
    connectorAndHint: 'All conditions must be met',
    connectorOrHint: 'At least one condition must be met',
    // Bulk dynamic fields editor (MasterCrudBulkDynamicEditor)
    close: 'Close',
    bulkEditTitle: 'Edit dynamic fields',
    bulkLoadingFields: 'Loading dynamic fields...',
    bulkLoadError: 'Error loading dynamic entities',
    bulkEditingIntroPrefix: 'Changes will be applied to',
    bulkEditingIntroSuffix: 'selected records. Fill in only the fields you want to modify; empty fields will not be changed.',
    bulkNoDynamicEntities: 'There are no dynamic entities configured for this module.',
    bulkMultiRecordNote: 'Only the main row of each record will be updated',
    bulkReadonlyEntity: 'This entity is read-only',
    bulkChangesSummary: 'Summary of changes',
    bulkWillApply: 'Will apply',
    bulkFieldsLabel: 'field(s)',
    bulkToLabel: 'to',
    bulkRecordsLabel: 'record(s)',
    bulkProcessing: 'Processing records...',
    bulkSucceededLabel: 'successful',
    bulkFailedLabel: 'failed',
    bulkOperationCompleted: 'Operation completed',
    bulkOfLabel: 'of',
    bulkRecordsPlural: 'records',
    bulkRecordsWithError: 'Records with errors',
    recordIdCsv: 'Record ID',
    reasonCsv: 'Reason',
    bulkPreview: 'Preview',
    bulkApplyTo: 'Apply to',
    bulkCancelOperation: 'Cancel operation',
    bulkExportErrorsCsv: 'Export errors CSV',
    bulkConfirmTitle: 'Confirm bulk modification',
    bulkConfirmLine1Prefix: 'You are about to modify',
    bulkConfirmLine1Suffix: 'record(s). This action cannot be undone.',
    bulkConfirmTypePrompt: 'To confirm, type',
    bulkConfirmAriaLabel: 'Bulk modification confirmation',
    showAdvancedSearch: 'Show advanced search',
    hideAdvancedSearch: 'Hide advanced search',
    filter: 'Filter',
    hideFilter: 'Hide filter',
    editing: 'Editing',
    creating: 'Creating',
    companyStatus: 'Enablement in the selected company',
    enabledInCompany: 'Enabled in the selected company',
    notEnabledInCompany: 'Not enabled in the selected company',
    enabled: 'Enabled',
    notEnabled: 'Not enabled',
    masterRecord: 'Master record',
    unsavedTitle: 'You have unsaved changes',
    unsavedDesc: 'If you leave now you will lose the following changes. Do you want to save them before returning to the list?',
    modifiedFields: 'Modified fields',
    emptyValue: 'empty',
    keepEditing: 'Keep editing',
    discardExit: 'Discard and exit',
    saveExit: 'Save and exit',
  }
};

/**
 * MasterCrud - Componente genérico para gestión de entidades (CRUD)
 */
export const MasterCrud = <T extends Record<string, any>>({
  title,
  entityName,
  fields,
  service: serviceProp,
  masterPattern,
  idField = 'id',
  pageSize = 10,
  showCreateButton = true,
  showSearch = true,
  showRowActions = true,
  allowDelete = true,
  softDelete = true,
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
  onError,
  createButtonText,
  saveButtonText,
  cancelButtonText,
  emptyMessage,
  className = '',
  id,
  renderToolbar,
  rowActions,
  formWidth = '600px',
  formColumns = 2,
  showViewToggle = true,
  showAdvancedSearch = true,
  showExport = false,
  onExport,
  onBack,
  formTabs,
  showValidationErrorDialog = true,
  columnFilterableFields,
  defaultViewScope = 'all',
  filterableFields,
  useToolbar = true,
  onViewChange,
  enableGridInfiniteScroll = true,
  onBeforeCreate,
  onBeforeUpdate,
  onBeforeDelete,
  locale = 'es',
  uiPrefix = 'mastercrud',
  fieldsPrefix,
  externalTranslations,
  t: tProp,
  permissions,
  internalErrorHandling = true,
  actions = [],
  globalActions = [],
  batchActions: batchActionsProp = [],
  emptyStateConfig,
  renderCardContent,
  renderForm,
  showDefaultEditAction = true,
  maximizeEditModal = false,
  maximizeCustomModal = true,
  fetchDetailOnEdit = true,
  navigationType: navigationTypeProp,
  enableMultiSelect = false,
  showAdvancedSearchConnectors = false,
  maxAdvancedFilters = 4,
  initialFiltersVisible = true,
  headerBadges,
  renderHeaderExtra,
  activeByCompany = false,
  companyRequired = false,
  companies = [],
  onCompanyChange,
  onConfirmLinking,
  isCompanyLinked,
  showGlobalValues: showGlobalValuesProp,
  onSearchMatches,
  dynamicFeatureCode,
  hideDynamicTabs = false,
  allowStatusReversion = true,
  statusField = 'isActive',
  deactivateTooltip,
  activateTooltip,
  onBulkUpdateComplete,
  auditHistory,
  featCodeFileManager,
  fileManagerConfig,
  fileAcceptedExtensions,
  fileMaxSizeMB,
  fileTotalMaxSizeMB,
  fileMultiple,
  detailConfig,
}: MasterCrudProps<T>) => {
  // ===== MOTOR DE TRADUCCIÓN =====
  const { t } = useMasterCrudTranslation({
    locale,
    uiPrefix,
    fieldsPrefix,
    externalTranslations,
    internalTranslations,
    tProp: tProp,
  });

  // ===== RESOLUCIÓN DE SERVICIO (service | masterPattern) =====
  // Usamos las propiedades primitivas de masterPattern como deps (no el objeto completo)
  // para que un masterPattern inline no cause una nueva instancia del adapter en cada render.
  // getHeaders y mapSearchRequest deben ser referencias estables (definidas fuera del JSX).
  const _resolvedService = React.useMemo((): CrudService<T> => {
    if (serviceProp) return serviceProp;
    if (masterPattern) return new MasterPatternCrudAdapter<T>(masterPattern, fields);
    throw new Error('[MasterCrud] Debes proporcionar "service" o "masterPattern".');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceProp, masterPattern?.baseUrl, masterPattern?.resourcePath, masterPattern?.masterType, masterPattern?.getHeaders, masterPattern?.mapSearchRequest]);
  // Sombrea la prop para que todo el código existente del componente funcione sin cambios
  const service = _resolvedService;

  // Dev warning: ambas props definidas (service tiene precedencia; masterPattern será ignorado)
  if (process.env.NODE_ENV !== 'production' && serviceProp && masterPattern) {
    console.warn('[MasterCrud] Se definieron "service" y "masterPattern" simultáneamente. "service" tiene precedencia y "masterPattern" será ignorado.');
  }

  // ===== CALLBACKS ESTABILIZADOS (Definidos pronto para evitar TDZ) =====
  const getErrorMessage = useCallback((error: unknown): string => {
    // Formato: string directo
    if (typeof error === 'string' && error) return error;

    if (typeof error === 'object' && error !== null) {
      const errObj = error as any;

      // PRIORIDAD 1 — Campos de alta prioridad directamente en el error.
      // Se comprueban ANTES de `message` para no capturar el texto genérico de Axios
      // ("Request failed with status code 4xx") cuando el servidor ya envió un mensaje específico.
      //   - msgError  → formato interno Siesa
      //   - detail    → RFC 7807 Problem Details (APIs .NET, Python, etc.)
      if (errObj.msgError) return String(errObj.msgError);
      if (errObj.detail) return String(errObj.detail);

      // PRIORIDAD 2 — Cuerpo del servidor en response.data (error envuelto por Axios/Fetch).
      // Cubre el caso en que el servicio NO re-lanza response.data sino el AxiosError completo.
      const responseData = errObj.response?.data;
      if (responseData && typeof responseData === 'object') {
        const fromData = responseData.msgError || responseData.detail || responseData.message || responseData.title;
        if (fromData) return String(fromData);
      }
      if (typeof responseData === 'string' && responseData) return responseData;

      // PRIORIDAD 3 — message estándar de JS Error.
      // También está presente en AxiosError, pero como mensaje genérico ("Request failed..."),
      // por eso se evalúa solo después de verificar response.data.
      if (errObj.message) return String(errObj.message);

      // PRIORIDAD 4 — title como último recurso descriptivo (RFC 7807)
      if (errObj.title) return String(errObj.title);
    }

    return t('defaultError');
  }, [t]);

  // ===== REFS PARA ESTABILIZAR CALLBACKS (Evitar bucles infinitos en loadData) =====
  const serviceRef = useRef(service);
  const getErrorMessageRef = useRef(getErrorMessage);

  useEffect(() => { serviceRef.current = service; }, [service]);
  useEffect(() => { getErrorMessageRef.current = getErrorMessage; }, [getErrorMessage]);
  const canRead = permissions?.canRead ?? true;
  const canCreate = permissions?.canCreate ?? showCreateButton;
  const canUpdate = permissions?.canUpdate ?? showRowActions;
  const canDelete = permissions?.canDelete ?? allowDelete;
  const canChangeStatus = permissions?.canChangeStatus ?? true;
  const canOverrideMultiCompany = permissions?.canOverrideMultiCompany ?? true;
  const canBulkUpdate = permissions?.canBulkUpdate ?? false;

  // En modo MasterPattern, DELETE no soporta reactivación → forzar false
  const effectiveAllowStatusReversion = masterPattern ? false : allowStatusReversion;

  // ===== RESOLUCIÓN DINÁMICA DE TÍTULOS =====
  const finalTitle = t('title', title);
  const finalEntityName = t('entityName', entityName);

  // ===== CONFIGURACIÓN DE NAVEGACIÓN =====
  // Por defecto la navegación al detalle es 'page' (del listado a la página de edición),
  // no modal ni sidebar. El consumidor puede sobrescribirlo con la prop navigationType.
  const navigationType = navigationTypeProp ?? 'page';

  // ===== LÓGICA DE COMPAÑÍAS AUTOMÁTICAS =====
  const globalCompany = React.useMemo(() => ({ uuid: '000', code: '000', name: 'Global' }), []);

  const allCompanies = React.useMemo(() => {
    if (!activeByCompany) return [];
    if (companyRequired) return [...companies];
    return [globalCompany, ...companies];
  }, [activeByCompany, companyRequired, companies, globalCompany]);

  // ===== ESTADOS =====
  const [viewState, setViewState] = useState<ViewState>('LIST');
  const [selectedRecord, setSelectedRecord] = useState<T | null>(null);
  const [globalRecordId, setGlobalRecordId] = useState<string | number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<MasterCrudCompany | undefined>(() => {
    if (!activeByCompany) return undefined;

    // 1. Si hay una compañía seleccionada por defecto, usarla
    const defaultCompany = companies.find(c => c.isSelectedByDefault);
    if (defaultCompany) return defaultCompany;

    // 2. Si companyRequired es true y hay compañías, seleccionar la primera
    if (companyRequired && companies.length > 0) return companies[0];

    // 3. Fallback: Global (solo si no es obligatoria una compañía específica)
    return companyRequired ? undefined : allCompanies[0];
  });
  const [toastConfig, setToastConfig] = useState<{ show: boolean; message: string; color: ToastColor; }>({ show: false, message: '', color: 'zinc', });
  const showToast = useCallback((message: string, color: ToastColor = 'zinc') => { setToastConfig({ show: true, message, color }); setTimeout(() => { setToastConfig((prev) => ({ ...prev, show: false })); }, 5000); }, []);
  const hideToast = useCallback(() => { setToastConfig((prev) => ({ ...prev, show: false })); }, []);

  const [linkConfirmState, setLinkConfirmState] = useState<{ show: boolean; company: MasterCrudCompany | null }>({ show: false, company: null });

  const [selectedRecords, setSelectedRecords] = useState<T[]>([]);
  const selectedIds = React.useMemo(() => selectedRecords.map(r => (r as any)[idField]), [selectedRecords, idField]);

  const [isAdvancedSearchActive, setIsAdvancedSearchActive] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilter[]>([]);

  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<string>('all');
  const searchFieldRef = useRef<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(pageSize);
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [viewType, setViewType] = useState<ToolbarViewType>('list');
  // Filtros por columna (server-side, viajan por el canal advancedFilters — retrocompatible).
  const [columnFilters, setColumnFilters] = useState<Record<string, { value: string; operator: 'contains' | 'equals' }>>({});
  // Alcance de filas en modo sobrescritura multicompañía (Global + compañía).
  const [viewScope, setViewScope] = useState<'all' | 'company'>(defaultViewScope);
  // Modal de cambios sin guardar (dirty state) al Volver (S2.8).
  const [dirtyModal, setDirtyModal] = useState<{ show: boolean; changes: { label: string; from: any; to: any }[] }>({ show: false, changes: [] });
  // Menú "Más acciones" del detalle (desplegable a la izquierda, como el listado).
  const [detailActionsOpen, setDetailActionsOpen] = useState(false);
  const detailActionsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!detailActionsOpen) return;
    const onDocClick = (e: MouseEvent) => { if (detailActionsRef.current && !detailActionsRef.current.contains(e.target as Node)) setDetailActionsOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDetailActionsOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
  }, [detailActionsOpen]);

  const [customModalState, setCustomModalState] = useState<{ show: boolean; content: React.ReactNode | null; title: string; }>({ show: false, content: null, title: '', });
  const [linkModalState, setLinkModalState] = useState<{ show: boolean; record: T | null; variant: 'success' | 'manual' }>({ show: false, record: null, variant: 'manual' });
  const [showGlobalResetConfirm, setShowGlobalResetConfirm] = useState(false);
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const [showGlobalValues, setShowGlobalValues] = useState(showGlobalValuesProp ?? false);
  const [matchModalState, setMatchModalState] = useState<{ show: boolean; matches: MatchResult[]; fieldName: string }>({ show: false, matches: [], fieldName: '' });
  const [previewModalState, setPreviewModalState] = useState<{ show: boolean; record: T | null; loading: boolean }>({ show: false, record: null, loading: false });
  const [auditModalState, setAuditModalState] = useState<{ show: boolean; record: T | null }>({ show: false, record: null });
  const [showBulkDynamicEditor, setShowBulkDynamicEditor] = useState(false);
  const formRef = useRef<any>(null);

  // ===== ESTADO DE GESTIÓN DE ARCHIVOS (modo automático via service) =====
  const [fileResources, setFileResources] = useState<GalleryResource[]>([]);
  const [fileResourcesLoading, setFileResourcesLoading] = useState(false);

  // ===== EFECTO DE AUTO-SELECCIÓN DE COMPAÑÍA (Carga asíncrona) =====
  useEffect(() => {
    if (activeByCompany && companies.length > 0 && !selectedCompany) {
      // 1. Prioridad: Compañía marcada como default
      const defaultCompany = companies.find(c => c.isSelectedByDefault);
      if (defaultCompany) {
        setSelectedCompany(defaultCompany);
        return;
      }

      // 2. Prioridad: Si es requerida, forzar la primera disponible
      if (companyRequired) {
        setSelectedCompany(companies[0]);
        return;
      }

      // 3. Fallback: Global (si no es requerida una específica)
      if (!companyRequired) {
        setSelectedCompany(allCompanies[0]);
      }
    }
  }, [activeByCompany, companyRequired, companies, selectedCompany, allCompanies]);

  // ===== CARGA AUTOMÁTICA DE ARCHIVOS AL SELECCIONAR REGISTRO =====
  useEffect(() => {
    if (!featCodeFileManager || !service.getFiles) return;
    const recordId = selectedRecord ? (selectedRecord as any)[idField] : null;
    if (!recordId) { setFileResources([]); return; }

    setFileResourcesLoading(true);
    service.getFiles(recordId, selectedCompany)
      .then(files => setFileResources(files))
      .catch(() => setFileResources([]))
      .finally(() => setFileResourcesLoading(false));
  }, [selectedRecord, idField, featCodeFileManager, selectedCompany]);

  // ===== HELPERS BÁSICOS =====
  const getCompanyPrimaryData = useCallback(() => selectedCompany, [selectedCompany]);

  // ===== LÓGICA DE VINCULACIÓN AUTOMÁTICA (Retrocompatible) =====
  const checkLinkStatus = useCallback((recordId: string | number, companyUuid: string | number): boolean => {
    // 1. Si es la compañía seleccionada actualmente en el toolbar, se considera vinculada (contexto activo)
    if (selectedCompany && String(selectedCompany.uuid) === String(companyUuid)) {
      return true;
    }

    // 2. Prioridad: Callback explícito del usuario
    if (isCompanyLinked) {
      return isCompanyLinked(recordId, companyUuid);
    }

    // 3. Fallback: Buscar en el registro seleccionado (Modo Edición/Detalle)
    const currentRecordId = selectedRecord ? (selectedRecord as any)[idField] : null;
    if (selectedRecord && String(currentRecordId) === String(recordId)) {
      const linked = (selectedRecord as any)._linkedCompanies;
      if (Array.isArray(linked)) {
        return linked.some(uuid => String(uuid) === String(companyUuid));
      }
    }

    // 3. Fallback: Buscar en el listado general (Modo Tabla/Grid)
    const recordInList = data.find(r => String((r as any)[idField]) === String(recordId));
    if (recordInList) {
      const linked = (recordInList as any)._linkedCompanies;
      if (Array.isArray(linked)) {
        return linked.some(uuid => String(uuid) === String(companyUuid));
      }
    }

    return false;
  }, [isCompanyLinked, selectedRecord, idField, data]);

  // Predicado por fila para la columna de estado por compañía del listado (N9).
  // A diferencia de checkLinkStatus, NO asume que la compañía activa está siempre habilitada:
  // refleja el estado real del registro en esa compañía.
  const isRecordCompanyEnabled = useCallback((row: any): boolean => {
    if (!selectedCompany || String(selectedCompany.uuid) === '000') return true;
    const rid = row?.[idField];
    if (isCompanyLinked) return isCompanyLinked(rid, selectedCompany.uuid);
    const linked = row?._linkedCompanies;
    if (Array.isArray(linked)) return linked.some((u: any) => String(u) === String(selectedCompany.uuid));
    if (typeof row?._enabled === 'boolean') return row._enabled;
    if (typeof row?.enabled === 'boolean') return row.enabled;
    return true;
  }, [selectedCompany, idField, isCompanyLinked]);

  // Filtros por columna activos por defecto: si el consumidor no especifica columnas,
  // se habilita el embudo en todos los campos visibles filtrables (no lookup). Retrocompatible.
  const effectiveColumnFilterableFields = React.useMemo(() => {
    if (columnFilterableFields) return columnFilterableFields;
    if (filterableFields) return filterableFields;
    return fields
      .filter(f => f.type !== 'lookup' && f.config?.searchable !== false)
      .map(f => f.accessorKey as string);
  }, [columnFilterableFields, filterableFields, fields]);

  // Mapa uuid → habilitado para el CompanySelector del detalle (S2.7): muestra por compañía
  // si el registro está Habilitado / No habilitado.
  const detailCompanyEnabledMap = React.useMemo(() => {
    const map: Record<string, boolean> = {};
    const rid = globalRecordId ?? (selectedRecord as any)?.[idField];
    if (rid == null) return map;
    allCompanies.forEach((c) => {
      if (String(c.uuid) !== '000') map[String(c.uuid)] = checkLinkStatus(rid, c.uuid);
    });
    return map;
  }, [allCompanies, checkLinkStatus, globalRecordId, selectedRecord, idField]);

  const handleCompanyChange = useCallback(async (company: MasterCrudCompany, recordId?: string | number) => {
    const isGlobal = String(company.uuid) === '000';
    const currentId = recordId || (selectedRecord as any)?.[idField];

    if (viewState === 'EDIT' && currentId && activeByCompany && !isGlobal) {
      const linked = checkLinkStatus(globalRecordId || currentId, company.uuid);
      if (!linked) { setLinkConfirmState({ show: true, company }); return; }
    }

    setSelectedCompany(company);
    // Al cambiar de compañía, el alcance de filas se reinicia a su valor por defecto (N9).
    setViewScope(defaultViewScope);
    onCompanyChange?.(company, currentId);

    if (viewState === 'EDIT' && currentId) {
      setFormLoading(true);
      try {
        let record: T;
        const targetId = globalRecordId || currentId;
        if (service.getById) { record = await service.getById(targetId, company); }
        else {
          const response = await service.getAll({ page: 1, limit: 1, filters: { [idField]: targetId } }, company);
          record = response.data[0];
        }
        if (record) setSelectedRecord(record);
      } catch {
        showToast(t('errorReload', 'Error al recargar el registro'), 'red');
      } finally { setFormLoading(false); }
    }

    if (!recordId && viewState === 'LIST') { setCurrentPage(1); setHasMore(true); }
  }, [onCompanyChange, viewState, activeByCompany, checkLinkStatus, globalRecordId, selectedRecord, idField, service, showToast, t]);

  const handleConfirmDynamicLink = useCallback(async () => {
    if (!linkConfirmState.company || !selectedRecord) return;
    const company = linkConfirmState.company;
    const currentId = (selectedRecord as any)[idField];
    const targetId = globalRecordId || currentId;

    setFormLoading(true);
    setLinkConfirmState({ show: false, company: null });

    try {
      // Obtener vinculaciones actuales y agregar la nueva para no perder las anteriores
      const currentLinks = (selectedRecord as any)._linkedCompanies || [];
      const newLinks = Array.from(new Set([...currentLinks, company.uuid]));

      await onConfirmLinking?.(targetId, newLinks);
      setSelectedCompany(company);
      onCompanyChange?.(company, targetId);

      let record: T;
      if (service.getById) { record = await service.getById(targetId, company); }
      else {
        const response = await service.getAll({ page: 1, limit: 1, filters: { [idField]: targetId } }, company);
        record = response.data[0];
      }

      if (record) {
        setSelectedRecord(record);

        // Sincronizar también en el listado general (data)
        setData(prev => prev.map(item => {
          if (String((item as any)[idField]) === String(targetId)) {
            return { ...item, _linkedCompanies: newLinks, ...(record || {}) };
          }
          return item;
        }));

        showToast(t('successLinkDynamic', 'Registro habilitado y cargado'), 'green');
      }
    } catch (error) {
      showToast(getErrorMessage(error), 'red');
    } finally { setFormLoading(false); }
  }, [linkConfirmState.company, selectedRecord, idField, globalRecordId, onConfirmLinking, onCompanyChange, service, showToast, getErrorMessage, t]);

  const handleLinkRecords = useCallback(async (recordId: string | number, companyUuids: (string | number)[]) => {
    try {
      await onConfirmLinking?.(recordId, companyUuids);

      // Actualizar en el listado general
      setData(prev => prev.map(item => {
        if (String((item as any)[idField]) === String(recordId)) {
          return { ...item, _linkedCompanies: companyUuids };
        }
        return item;
      }));

      // Actualizar el registro seleccionado si es el mismo que se está vinculando
      if (selectedRecord && String((selectedRecord as any)[idField]) === String(recordId)) {
        setSelectedRecord(prev => prev ? { ...prev, _linkedCompanies: companyUuids } : null);
      }

      showToast(t('successLink', 'Habilitaciones actualizadas correctamente'), 'green');
    } catch (error) {
      showToast(getErrorMessage(error), 'red');
      // El backend puede haber guardado vinculaciones parciales antes del error.
      // Se cede al event loop para que React procese actualizaciones de estado pendientes,
      // luego se re-lee el registro para reflejar el estado real.
      setTimeout(async () => {
        try {
          const company = getCompanyPrimaryData();
          const svc = serviceRef.current;
          const refreshed = svc.getById
            ? await svc.getById(recordId, company)
            : (await svc.getAll({ page: 1, limit: 1, filters: { [idField]: recordId } }, company)).data[0];
          if (refreshed) {
            const actualLinks = (refreshed as any)._linkedCompanies;
            if (Array.isArray(actualLinks)) {
              setData(prev => prev.map(item =>
                String((item as any)[idField]) === String(recordId)
                  ? { ...item, _linkedCompanies: actualLinks }
                  : item
              ));
              if (selectedRecord && String((selectedRecord as any)[idField]) === String(recordId)) {
                setSelectedRecord(prev => prev ? { ...prev, _linkedCompanies: actualLinks } : null);
              }
            }
          }
        } catch { /* Silencioso: no altera el mensaje de error principal */ }
      }, 0);
    }
  }, [onConfirmLinking, idField, selectedRecord, getCompanyPrimaryData, showToast, getErrorMessage, t]);

  const handleSearchMatches = useCallback(async (field: MasterCrudField<T>, value: string) => {
    if (!onSearchMatches) return;
    try {
      const results = await onSearchMatches(field, value);
      if (results && results.length > 0) {
        // `header` es obligatorio en el contrato, pero lo define la configuración
        // del consumidor: TypeScript no garantiza que llegue en runtime. Se
        // normaliza aquí para no guardar un `undefined` en un estado tipado como
        // `string`; el MatchModal degrada solo a su título genérico.
        const fieldName = typeof field.header === 'string' ? field.header : '';
        setMatchModalState({ show: true, matches: results, fieldName });
      }
    } catch (error) {
      console.error('Error searching matches:', error);
    }
  }, [onSearchMatches]);

  const handleViewMatch = useCallback(async (match: MatchResult) => {
    setPreviewModalState({ show: true, record: null, loading: true });
    try {
      let record: T;
      if (service.getById) { record = await service.getById(match.id, getCompanyPrimaryData()); }
      else {
        const response = await service.getAll({ page: 1, limit: 1, filters: { [idField]: match.id } }, getCompanyPrimaryData());
        record = response.data[0];
      }

      if (record) { setPreviewModalState({ show: true, record, loading: false }); }
      else {
        showToast(t('errorRecordNotFound', 'No se pudo encontrar el registro seleccionado'), 'red');
        setPreviewModalState(prev => ({ ...prev, show: false, loading: false }));
      }
    } catch (error) {
      showToast(t('errorDataLoad', 'Error al cargar los datos del registro'), 'red');
      setPreviewModalState(prev => ({ ...prev, show: false, loading: false }));
    }
  }, [service, getCompanyPrimaryData, idField, showToast, t]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    const handleDeviceChange = (e: MediaQueryListEvent | MediaQueryList) => { setViewType(e.matches ? 'grid' : 'list'); };
    handleDeviceChange(mediaQuery);
    mediaQuery.addEventListener('change', handleDeviceChange);
    return () => mediaQuery.removeEventListener('change', handleDeviceChange);
  }, []);

  const handleConfirmGlobalReset = useCallback(() => {
    formRef.current?.revertAllToGlobal();
    setShowGlobalResetConfirm(false);
    showToast(t('successRevert', 'Campos restaurados a valores globales'), 'zinc');
  }, [showToast, t]);

  const searchFieldOptions = React.useMemo(() => {
    let targetFields = fields;
    if (filterableFields && filterableFields.length > 0) {
      targetFields = fields.filter(f => filterableFields.includes(f.accessorKey as string));
    } else {
      targetFields = fields.filter(f => f.config?.searchable !== false);
    }
    const options = targetFields.map(f => ({
      value: f.accessorKey as string,
      label: t(f.accessorKey as string, f.header)
    }));
    return [{ value: 'all', label: t('all') }, ...options];
  }, [fields, filterableFields, t]);

  useEffect(() => {
    if (searchField === 'all' && fields.length > 0) {
      const targetFields = filterableFields && filterableFields.length > 0
        ? fields.filter(f => filterableFields.includes(f.accessorKey as string))
        : fields.filter(f => f.config?.searchable !== false);

      if (targetFields.length > 0) {
        const firstField = targetFields[0].accessorKey as string;
        setSearchField(firstField);
        searchFieldRef.current = firstField;
      }
    }
  }, [fields, filterableFields]);

  const sortFieldOptions = React.useMemo(() => {
    const targetFields = fields.filter(f => f.config?.sortable === true);
    const options = targetFields.map(f => ({
      value: f.accessorKey as string,
      label: t(f.accessorKey as string, f.header),
      field: f.accessorKey as string,
      direction: 'asc' as const,
    }));
    return [{ value: '', label: t('noSort'), field: '', direction: 'asc' as const }, ...options];
  }, [fields, t]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => { if (entries[0].isIntersecting && hasMore) { setCurrentPage(prevPage => prevPage + 1); } });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const loadData = useCallback(async () => {
    if (!canRead) return;
    if (activeByCompany && companyRequired && !selectedCompany) return;
    if (viewType === 'grid' && enableGridInfiniteScroll && currentPage > 1 && !hasMore) return;

    setLoading(true);
    try {
      const currentSearchField = searchFieldRef.current;

      // Filtros por columna → AdvancedFilter[] (viajan por el canal de búsqueda avanzada
      // existente, retrocompatible con endpoints que ya soportan advancedFilters).
      const columnAdvancedFilters: AdvancedFilter[] = Object.entries(columnFilters).map(([field, { value, operator }]) => ({
        id: `col_${field}`,
        field,
        connector: 'AND' as const,
        operator,
        value,
      }));
      const mergedAdvancedFilters = [
        ...(isAdvancedSearchActive ? advancedFilters : []),
        ...columnAdvancedFilters,
      ];

      // Alcance de filas (multicompañía por sobrescritura), aditivo y server-side.
      const isCompanyScope = activeByCompany && selectedCompany && String(selectedCompany.uuid) !== '000';
      const scopeFilter = isCompanyScope ? { viewScope } : undefined;

      const hasFilters = mergedAdvancedFilters.length > 0 || !!scopeFilter;
      const params: GetAllParams = {
        page: currentPage,
        limit: internalPageSize,
        search: isAdvancedSearchActive ? undefined : (searchTerm || undefined),
        searchField: isAdvancedSearchActive ? undefined : (currentSearchField !== 'all' ? currentSearchField : undefined),
        sortBy: sortColumn,
        sortDirection: sortDirection || undefined,
        filters: hasFilters
          ? {
              ...(mergedAdvancedFilters.length > 0 ? { advancedFilters: mergedAdvancedFilters } : {}),
              ...(scopeFilter || {}),
            }
          : undefined,
      };

      const response = await serviceRef.current.getAll(params, getCompanyPrimaryData());

      if (viewType === 'grid' && enableGridInfiniteScroll && currentPage > 1) { setData(prev => [...prev, ...response.data]); }
      else { setData(response.data); }

      setTotal(response.total);
      const loadedCount = (currentPage - 1) * internalPageSize + response.data.length;
      setHasMore(loadedCount < response.total);

    } catch (error) {
      if (internalErrorHandling) showToast(getErrorMessageRef.current(error), 'red');
      onError?.(error as Error, 'fetch');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, internalPageSize, searchTerm, sortColumn, sortDirection, viewType, enableGridInfiniteScroll, isAdvancedSearchActive, advancedFilters, columnFilters, viewScope, activeByCompany, selectedCompany, getCompanyPrimaryData, canRead]);

  useEffect(() => { loadData(); }, [loadData]);

  // Cambio de un filtro por columna (server-side): actualiza estado y recarga desde la página 1.
  const handleColumnFilterChange = useCallback((field: string, value: string, operator: 'contains' | 'equals') => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (value === '' || value == null) delete next[field];
      else next[field] = { value, operator };
      return next;
    });
    setCurrentPage(1);
    setHasMore(true);
  }, []);

  // Cambio de alcance de filas (multicompañía por sobrescritura), server-side.
  const handleViewScopeChange = useCallback((scope: 'all' | 'company') => {
    setViewScope(scope);
    setCurrentPage(1);
    setHasMore(true);
  }, []);

  // Detección del modo sobrescritura multicompañía con compañía específica seleccionada.
  // En modo "compañía requerida" no aplica el modelo de sobreescritura Global→Sucursal:
  // los registros son propios de la compañía, por lo que NO se muestran la columna de estado
  // por compañía ni los switches de alcance ("Registros a mostrar").
  const isCompanyScopeActive = !!(activeByCompany && !companyRequired && selectedCompany && String(selectedCompany.uuid) !== '000');

  const handleSearch = useCallback((term: string) => { setSearchTerm(term); setCurrentPage(1); setHasMore(true); }, []);
  const handleSort = useCallback((column: string, direction: 'asc' | 'desc' | null) => { setSortColumn(direction ? column : undefined); setSortDirection(direction); }, []);
  const handleSortChange = useCallback((value: string) => { const newSortColumn = value || undefined; setSortColumn(newSortColumn); if (newSortColumn && !sortDirection) setSortDirection('asc'); setCurrentPage(1); setHasMore(true); }, [sortDirection]);
  const handleSortDirectionChange = useCallback((direction: 'asc' | 'desc') => { setSortDirection(direction); if (sortColumn) { setCurrentPage(1); setHasMore(true); } }, [sortColumn]);
  const handleFilterChange = useCallback((value: string) => { setSearchField(value); searchFieldRef.current = value; setSearchTerm(''); if (searchTerm !== '') { setCurrentPage(1); setHasMore(true); } }, [searchTerm]);
  const handleViewChange = useCallback((view: ToolbarViewType) => { setViewType(view); setCurrentPage(1); setHasMore(true); onViewChange?.(view); }, [onViewChange]);
  const handlePageChange = useCallback((page: number) => { setCurrentPage(page); }, []);
  const handlePageSizeChange = useCallback((size: number) => { setInternalPageSize(size); setCurrentPage(1); setHasMore(true); }, []);
  const handleCreate = useCallback(() => { setSelectedRecord(null); setGlobalRecordId(null); setViewState('CREATE'); }, []);
  const handleEdit = useCallback(async (record: T) => {
    const id = (record as any)[idField];
    setGlobalRecordId(id);
    setViewState('EDIT');

    if (fetchDetailOnEdit) {
      setSelectedRecord(null);
      setFormLoading(true);
      try {
        let detailedRecord: T;
        if (service.getById) { detailedRecord = await service.getById(id, getCompanyPrimaryData()); }
        else {
          const response = await service.getAll({ page: 1, limit: 1, filters: { [idField]: id } }, getCompanyPrimaryData());
          detailedRecord = response.data[0];
        }
        if (detailedRecord) { setSelectedRecord(detailedRecord); }
        else { setSelectedRecord(record); }
      } catch (error) {
        if (internalErrorHandling) showToast(t('errorDetailLoad', 'Error al cargar el detalle del registro'), 'red');
        setSelectedRecord(record);
      } finally { setFormLoading(false); }
    } else { setSelectedRecord(record); }
  }, [idField, fetchDetailOnEdit, service, getCompanyPrimaryData, internalErrorHandling, showToast, t]);
  const handleFormCancel = useCallback(() => { setViewState('LIST'); setSelectedRecord(null); setGlobalRecordId(null); }, []);
  // Volver con guardia de cambios sin guardar (S2.8): si el formulario está "sucio", pide confirmación.
  const handleBackGuarded = useCallback(() => {
    const changes = (formRef.current?.getChanges?.() ?? []) as { label: string; from: any; to: any }[];
    if (changes.length > 0) setDirtyModal({ show: true, changes });
    else handleFormCancel();
  }, [handleFormCancel]);

  const handleSelectionChange = useCallback((newIds: (string | number)[]) => {
    setSelectedRecords(prev => {
      const currentDataIds = data.map(r => (r as any)[idField]);
      const idsToUnselect = currentDataIds.filter(id => !newIds.includes(id));
      let updated = prev.filter(r => !idsToUnselect.includes((r as any)[idField]));
      const idsToAdd = newIds.filter(id => !prev.some(r => (r as any)[idField] === id));
      const recordsToAdd = data.filter(r => idsToAdd.includes((r as any)[idField]));
      return [...updated, ...recordsToAdd];
    });
  }, [data, idField]);

  const toggleAdvancedSearch = useCallback(() => {
    setIsAdvancedSearchActive(prev => { if (prev) { setAdvancedFilters([]); setCurrentPage(1); } return !prev; });
  }, []);

  const handleAdvancedSearch = useCallback((filters: AdvancedFilter[]) => { setAdvancedFilters(filters); setCurrentPage(1); setHasMore(true); }, []);

  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; record: T | null }>({ show: false, record: null });
  const handleDeleteClick = useCallback(async (record: T) => { if (onBeforeDelete && !(await onBeforeDelete(record))) return; setDeleteConfirm({ show: true, record }); }, [onBeforeDelete]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm.record) return;
    setLoading(true);
    try {
      const recordId = (deleteConfirm.record as any)[idField];
      const companyData = getCompanyPrimaryData();
      const isActive = !!((deleteConfirm.record as any)[statusField] ?? true);

      let operation: MasterCrudOperationType = 'delete';
      if (softDelete) {
        operation = isActive ? 'deactivate' : 'activate';
      }

      // Enviar contexto de compañía y estado actual al fetch de eliminación
      // El tercer parámetro indica el tipo de operación (física o cambio de estado)
      await service.delete(recordId, { ...companyData, isActive, record: deleteConfirm.record, companyRequired }, operation);
      onDeleteSuccess?.(recordId);

      const isBranchContext = activeByCompany && !companyRequired && selectedCompany && String(selectedCompany.uuid) !== '000';
      let successMsg = t('successDelete', 'Registro eliminado correctamente');

      if (softDelete && isBranchContext) {
        if (isActive) {
          successMsg = t('successUnlink', 'Registro deshabilitado correctamente');
        } else {
          successMsg = t('successLink', 'Registro habilitado correctamente');
        }
      } else if (softDelete && !isBranchContext) {
        successMsg = isActive
          ? t('successDeactivate', 'Registro desactivado correctamente')
          : t('successActivate', 'Registro activado correctamente');
      }

      showToast(successMsg, 'green');
      setDeleteConfirm({ show: false, record: null });

      if (operation === 'delete') {
        // En contexto de eliminación física: Quitar el registro de la tabla/card
        setTotal(prev => Math.max(0, prev - 1));
        setData(prevData => {
          const newData = prevData.filter(item => (item as any)[idField] !== recordId);
          if (viewType === 'list' && newData.length === 0 && currentPage > 1) { setCurrentPage(prev => prev - 1); }
          return newData;
        });
        setSelectedRecords(prev => prev.filter(r => (r as any)[idField] !== recordId));
      } else {
        // En contexto de cambio de estado: Solo refrescar para reflejar cambio
        loadData();
      }
    } catch (error) {
      if (internalErrorHandling) showToast(getErrorMessage(error), 'red');
      onError?.(error as Error, 'delete');
    } finally { setLoading(false); }
  }, [deleteConfirm.record, idField, service, onDeleteSuccess, onError, showToast, getErrorMessage, internalErrorHandling, viewType, currentPage, getCompanyPrimaryData, t, loadData, softDelete, statusField, activeByCompany, companyRequired, selectedCompany]);

  const handleCustomAction = useCallback((action: MasterCrudAction<T>, record: T) => {
    const actionName = typeof action.name === 'function' ? action.name(record) : action.name;
    if (action.modalContent) { const content = typeof action.modalContent === 'function' ? action.modalContent(record) : action.modalContent; setCustomModalState({ show: true, content, title: actionName }); }
    else if (action.action) { action.action(record, getCompanyPrimaryData()); }
  }, [getCompanyPrimaryData]);

  const handleCloseCustomModal = useCallback(() => { setCustomModalState({ show: false, content: null, title: '' }); }, []);

  const getActionsDefinition = useCallback((): MasterCrudAction<T>[] => {
    const defaultActions: MasterCrudAction<T>[] = [];
    if (canRead && canUpdate && showDefaultEditAction) defaultActions.push({ id: 'edit', name: t('edit'), icon: <PencilIcon className="w-4 h-4" />, action: (r: T | T[]) => handleEdit(r as T), order: 100 });
    if (activeByCompany && !companyRequired) {
      defaultActions.push({
        id: 'link', name: t('link', 'Habilitar'), icon: <LinkIcon className="w-4 h-4" />,
        action: (r: T | T[]) => { setLinkModalState({ show: true, record: Array.isArray(r) ? r[0] : r, variant: 'manual' }); },
        order: 110
      });
    }

    const showSoftDelete = softDelete && canChangeStatus;
    const showHardDelete = !softDelete && canDelete;

    if (showSoftDelete || showHardDelete) {
      const isBranchContext = activeByCompany && selectedCompany && String(selectedCompany.uuid) !== '000';

      defaultActions.push({
        id: 'delete',
        name: (r) => {
          if (!r) return t('delete');
          const isActive = !!((r as any)[statusField] ?? true);
          const isBranchContext = activeByCompany && !companyRequired && selectedCompany && String(selectedCompany.uuid) !== '000';

          if (isActive) {
            // El botón de estado usa siempre terminología de Desactivar (Global y Sucursal).
            // La "habilitación por compañía" (Habilitar/Deshabilitar) es un concepto aparte
            // gestionado por la acción 'link' y el modal de habilitación.
            if (!isBranchContext) return softDelete ? t('deactivateRecord', 'Desactivar registro') : t('delete', 'Eliminar');
            // En Sucursal, respetamos el tooltip personalizado si existe; si no, 'Desactivar registro'.
            if (deactivateTooltip) return deactivateTooltip;
            return t('deactivateRecord', 'Desactivar registro');
          } else {
            // Si el registro está inactivo, mostramos siempre Activar
            if (!isBranchContext) return t('activate', 'Activar');
            if (activateTooltip) return activateTooltip;
            return t('activate', 'Activar');
          }
        },
        icon: (r) => {
          if (!softDelete) return <TrashIcon className="w-4 h-4" />;
          if (!r) return <TrashIcon className="w-4 h-4" />;
          const isActive = !!((r as any)[statusField] ?? true);
          const isBranchContext = activeByCompany && !companyRequired && selectedCompany && String(selectedCompany.uuid) !== '000';

          // 1. Si el registro está inactivo, mostramos siempre el icono de activación (Círculo con Check)
          if (!isActive) return <CheckCircleIcon className="w-4 h-4" />;

          // 2. Si el registro está activo y es softDelete:
          // En Global (!isBranchContext) o Sucursal, usamos MinusCircleIcon para representar 'Desactivar/Desvincular'
          // Reservamos TrashIcon solo para cuando softDelete es FALSE (borrado físico)
          return isBranchContext ? <NoSymbolIcon className="w-4 h-4" /> : (softDelete ? <NoSymbolIcon className="w-4 h-4" /> : <TrashIcon className="w-4 h-4" />);
        },
        action: (r: T | T[]) => handleDeleteClick(r as T),
        disabled: (r) => {
          if (!r) return false;
          const isActive = !!((r as any)[statusField] ?? true);
          // Si no se permite reversión y el registro ya está inactivo/desvinculado, deshabilitamos.
          if (effectiveAllowStatusReversion === false && !isActive) return true;
          return false;
        },
        visible: (r) => {
          if (!r) return true;
          const isActive = !!((r as any)[statusField] ?? true);
          // Si no se permite reversión y el registro ya está inactivo, OCULTAMOS el botón.
          if (effectiveAllowStatusReversion === false && !isActive) return false;
          return true;
        },
        order: 200
      });
    }
    if (auditHistory?.enabled) {
      defaultActions.push({
        id: 'audit',
        name: t('auditChanges', 'Auditoría de Cambios'),
        icon: <ClockIcon className="w-4 h-4" />,
        order: 150,
        displayType: 'menu',
        visible: (r: T) => !!(r as any).id || !!(r as any).uuid,
        action: (r: T | T[]) => setAuditModalState({ show: true, record: r as T }),
      });
    }

    const customActions = actions.map(action => ({ ...action, order: action.order ?? 0, action: (r: T | T[]) => handleCustomAction(action, r as T) }));

    return [...defaultActions, ...customActions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [canRead, canUpdate, showDefaultEditAction, canDelete, canChangeStatus, softDelete, actions, t, handleEdit, handleDeleteClick, handleCustomAction, activeByCompany, companyRequired, selectedCompany, deactivateTooltip, activateTooltip, effectiveAllowStatusReversion, statusField, auditHistory]);

  const actionsList = React.useMemo(() => getActionsDefinition(), [getActionsDefinition]);

  const combinedBatchActions = React.useMemo(() => {
    const explicitBatchActions = batchActionsProp.map(a => ({ ...a, action: () => { a.action?.(selectedRecords); setSelectedRecords([]); } }));
    const inferredBatchActions = actions.filter(a => a.isGroupAction).map(a => ({ ...a, action: () => { a.action?.(selectedRecords); setSelectedRecords([]); } }));
    return [...explicitBatchActions, ...inferredBatchActions];
  }, [batchActionsProp, actions, selectedRecords]);

  const handleBulkUpdateComplete = useCallback((result: BulkUpdateResult) => {
    loadData();
    setSelectedRecords([]);
    setShowBulkDynamicEditor(false);
    onBulkUpdateComplete?.(result);
  }, [loadData, onBulkUpdateComplete]);

  const handleFormSubmit = useCallback(async (formData: Partial<T>) => {
    setFormLoading(true);
    try {
      if (viewState === 'CREATE') {
        if (onBeforeCreate && !(await onBeforeCreate(formData))) { setFormLoading(false); return; }
        let dataToCreate = { ...formData };
        if (activeByCompany && selectedCompany && String(selectedCompany.uuid) !== '000') { (dataToCreate as any)._createInGlobal = true; }
        const created = await service.create(dataToCreate, getCompanyPrimaryData());
        console.log("%c[MASTER-CRUD] Registro base creado exitosamente con ID:", "color: #22c55e;", (created as any)[idField]);

        // Guardar entidades dinámicas si existen
        console.log("%c[MASTER-CRUD] Evaluando guardado de entidades dinámicas. FeatureCode:", "color: blue;", dynamicFeatureCode);
        if (dynamicFeatureCode && formRef.current?.saveDynamicEntities) {
          const dynamicSuccess = await formRef.current.saveDynamicEntities((created as any)[idField]);
          if (!dynamicSuccess) {
            showToast('Registro base creado, pero hubo un error con los datos dinámicos', 'red');
          }
        }

        // Guardar gestión de archivos si está habilitada
        if (featCodeFileManager && formRef.current?.saveFileManagement) {
          const fileSuccess = await formRef.current.saveFileManagement((created as any)[idField]);
          if (!fileSuccess) {
            showToast('Registro creado, pero hubo errores al gestionar los archivos', 'red');
          }
        }

        onCreateSuccess?.(created);
        showToast(t('successCreate', 'Registro creado correctamente'), 'green');
        loadData();
        if (activeByCompany && (!selectedCompany || String(selectedCompany.uuid) === '000')) {
          setSelectedRecord(created);
          setGlobalRecordId((created as any)[idField]);
          setViewState('EDIT');
          setLinkModalState({ show: true, record: created, variant: 'success' });
        } else if (detailConfig) {
          // Maestro-Detalle: quedarse en EDIT para que aparezca la sección de detalles
          setSelectedRecord(created);
          setGlobalRecordId((created as any)[idField]);
          setViewState('EDIT');
        } else { setViewState('LIST'); setSelectedRecord(null); setGlobalRecordId(null); }
      } else if (viewState === 'EDIT' && selectedRecord) {
        const recordId = (selectedRecord as any)[idField];
        let dataToUpdate: Partial<T> = { ...formData };
        if (activeByCompany && selectedCompany && String(selectedCompany.uuid) !== '000') {
          const overriddenFields = fields.filter(f => f.config?.allowMultiCompanyOverride).map(f => f.accessorKey as string);
          (dataToUpdate as any)._multicompanyMetadata = { isOverride: true, companyUuid: selectedCompany.uuid, overriddenFields };
        }
        if (onBeforeUpdate && !(await onBeforeUpdate(recordId, dataToUpdate))) { setFormLoading(false); return; }
        const updated = await service.update(recordId, dataToUpdate, getCompanyPrimaryData());
        console.log("%c[MASTER-CRUD] Registro base actualizado exitosamente. ID:", "color: #22c55e;", recordId);

        // Guardar entidades dinámicas si existen
        console.log("%c[MASTER-CRUD] Evaluando guardado de entidades dinámicas (EDIT). FeatureCode:", "color: blue;", dynamicFeatureCode);
        if (dynamicFeatureCode && formRef.current?.saveDynamicEntities) {
          const dynamicSuccess = await formRef.current.saveDynamicEntities(recordId);
          if (!dynamicSuccess) {
            showToast('Registro base actualizado, pero hubo un error con los datos dinámicos', 'red');
          }
        }

        // Guardar gestión de archivos si está habilitada
        if (featCodeFileManager && formRef.current?.saveFileManagement) {
          const fileSuccess = await formRef.current.saveFileManagement(recordId);
          if (!fileSuccess) {
            showToast('Registro actualizado, pero hubo errores al gestionar los archivos', 'red');
          }
        }

        if (!detailConfig) {
          setViewState('LIST');
          setSelectedRecord(null);
        } else {
          // Maestro-Detalle: actualizar selectedRecord con la respuesta del servidor
          // para que el formulario refleje los datos actualizados sin datos stale
          setSelectedRecord(updated ?? ({ ...selectedRecord, ...dataToUpdate } as T));
        }
        setData(prevData => prevData.map(item => (item as any)[idField] === recordId ? { ...item, ...dataToUpdate, ...(updated || {}) } : item));
        onUpdateSuccess?.(updated);
        showToast(t('successUpdate', 'Registro actualizado correctamente'), 'green');
      }
    } catch (error) {
      if (internalErrorHandling) showToast(getErrorMessage(error), 'red');
      onError?.(error as Error, viewState === 'CREATE' ? 'create' : 'update');
    } finally { setFormLoading(false); }
  }, [viewState, selectedRecord, idField, service, onCreateSuccess, onUpdateSuccess, onError, loadData, onBeforeCreate, onBeforeUpdate, showToast, getErrorMessage, internalErrorHandling, activeByCompany, selectedCompany, fields, getCompanyPrimaryData, t, detailConfig]);

  const totalPages = Math.ceil(total / internalPageSize);
  // Llena la altura disponible tanto en un contenedor flex-col (flex-1) como en uno de altura
  // explícita (h-full). Con un padre flex-col + hermanos (p. ej. un panel informativo en una
  // story), `flex-1` gana y respeta a los hermanos; con un padre de altura explícita, aplica
  // `h-full`. En ambos casos el header/footer de la tabla quedan fijos con scroll interno. Si el
  // contenedor no acota la altura, degrada a scroll de página (sin romper).
  const containerClasses = `flex flex-col w-full relative flex-1 min-h-0 h-full ${className}`.trim();

  // ===== CONFIGURACIÓN EFECTIVA DE GESTIÓN DE ARCHIVOS =====
  // Si el consumidor pasa fileManagerConfig manual, se usa tal cual (modo manual).
  // Si el servicio implementa los métodos de archivos, se construye automáticamente (modo automático).
  const effectiveFileManagerConfig = React.useMemo((): FileManagerConfig | undefined => {
    if (!featCodeFileManager) return undefined;
    if (fileManagerConfig) return fileManagerConfig;
    if (!service.getFiles) return undefined;

    return {
      resources: fileResources,
      isLoading: fileResourcesLoading,
      allowDeletion: !!service.deleteFile,
      showFileUploader: !!service.uploadFile,
      acceptedExtensions: fileAcceptedExtensions,
      maxFileSizeMB: fileMaxSizeMB,
      maxTotalSizeMB: fileTotalMaxSizeMB,
      multipleFiles: fileMultiple,
      onUpload: service.uploadFile
        ? async (file: File, recId: string | number) => {
          await service.uploadFile!(recId, file, getCompanyPrimaryData());
          const updated = await service.getFiles!(recId, getCompanyPrimaryData());
          setFileResources(updated);
        }
        : undefined,
      onDelete: service.deleteFile
        ? async (uuid: string, recId: string | number) => {
          await service.deleteFile!(recId, uuid, getCompanyPrimaryData());
          setFileResources(prev => prev.filter(r => r.uuid !== uuid));
        }
        : undefined,
      onAccept: service.saveFiles
        ? async (selected: GalleryResource[], recId: string | number) => {
          await service.saveFiles!(recId, selected, getCompanyPrimaryData());
        }
        : undefined,
    };
  }, [featCodeFileManager, fileManagerConfig, service, fileResources, fileResourcesLoading, getCompanyPrimaryData]);

  const defaultToolbar = useToolbar ? (
    <MasterCrudToolbar
      title={finalTitle}
      searchPlaceholder={t('search', 'Buscar')}
      searchValue={searchTerm}
      onSearchChange={handleSearch}
      showSearch={showSearch}
      filterOptions={searchFieldOptions}
      selectedFilter={searchField}
      onFilterChange={handleFilterChange}
      showFilter={showSearch && searchFieldOptions.length > 0}
      filterButtonText={t('searchBy')}
      sortOptions={sortFieldOptions}
      selectedSort={sortColumn}
      onSortChange={handleSortChange}
      sortDirection={sortDirection || 'asc'}
      onSortDirectionChange={handleSortDirectionChange}
      showSort={sortFieldOptions.length > 0}
      sortButtonText={t('sortBy')}
      viewType={viewType}
      onViewChange={handleViewChange}
      showViewToggle={showViewToggle}
      showAdvancedSearch={showAdvancedSearch}
      globalActions={globalActions}
      batchActions={combinedBatchActions}
      hasSelection={selectedRecords.length > 0}
      isAdvancedSearchActive={isAdvancedSearchActive}
      onAdvancedSearchToggle={toggleAdvancedSearch}
      initialFiltersVisible={initialFiltersVisible}
      activeByCompany={activeByCompany}
      companies={allCompanies}
      selectedCompany={selectedCompany}
      onCompanyChange={handleCompanyChange}
      showExport={showExport}
      onExport={onExport}
      showViewScope={isCompanyScopeActive}
      viewScope={viewScope}
      onViewScopeChange={handleViewScopeChange}
      onBack={onBack}
      t={t}
      extraContent={
        <div className="flex items-center gap-2">
          {dynamicFeatureCode && enableMultiSelect && canBulkUpdate && selectedRecords.length > 0 && (
            <Button
              type="plain"
              size="base"
              onClick={() => setShowBulkDynamicEditor(true)}
            >
              Editar campos dinamicos
            </Button>
          )}
          {canCreate && (
            <Button type="default" size="base" className="!text-sm font-medium" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={handleCreate}>
              {createButtonText || t('create')}
            </Button>
          )}
        </div>
      }
    />
  ) : (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4">
      <h1 className="text-xl font-semibold text-[#1355b7] dark:text-primary-custom-600">{finalTitle}</h1>
      {canCreate && (
        <Button type="default" size="base" className="!text-sm font-medium" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={handleCreate}>
          {createButtonText || t('create')}
        </Button>
      )}
    </div>
  );

  return (
    <div className={containerClasses} id={id} style={{ width: '100%', minWidth: '100%' }}>
      <Toast
        isVisible={toastConfig.show}
        message={toastConfig.message}
        color={toastConfig.color}
        onClose={hideToast}
        position="top-right"
        className="z-[9999]"
      />

      {/* Solo renderizamos el listado principal si NO estamos en modo página de creación/edición */}
      {!(navigationType === 'page' && (viewState === 'CREATE' || viewState === 'EDIT')) ? (
        <div className="flex-1 flex flex-col min-h-0 transition-all duration-300 overflow-hidden">
          {/* Contenedor centrado — ancho ideal max-w-7xl (1280px); alinea header y resultados (S2.0) */}
          <div className="flex-1 min-h-0 flex flex-col w-full max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6 gap-4 md:gap-6">
            <div className="flex-shrink-0">
              {renderToolbar ? renderToolbar({ onSearch: handleSearch, onCreate: handleCreate, searchTerm }) : defaultToolbar}
            </div>

            <div className="flex-1 min-h-0 flex overflow-hidden gap-4">
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {!canRead ? (
                <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                  <div className="w-full max-w-md">
                    <Info title={t('errorTitle')} description={t('noPermission')} color="red" />
                  </div>
                </div>
              ) : (
                <>
                  {isAdvancedSearchActive && (
                    <div className="mb-4 flex-shrink-0">
                      <MasterCrudAdvancedSearch fields={fields} onSearch={handleAdvancedSearch} onClose={toggleAdvancedSearch} initialSearchTerm={searchTerm} initialSearchField={searchField} showConnectors={showAdvancedSearchConnectors} maxFilters={maxAdvancedFilters} filterableFields={filterableFields} columnFilterableFields={effectiveColumnFilterableFields} t={t} />
                    </div>
                  )}

                  {viewType === 'list' ? (
                    data.length > 0 || loading ? (
                      <div className="flex flex-col flex-1 min-h-0 bg-white dark:bg-dark-bg-primary rounded-xl border border-border-primary dark:border-dark-border-primary overflow-hidden w-full">
                        <MasterCrudTable fields={fields} data={data} loading={loading} stickyHeader emptyMessage={emptyMessage || t('empty')} onSort={handleSort} sortColumn={sortColumn} sortDirection={sortDirection} pagination={{ currentPage, totalPages, onPageChange: handlePageChange, pageSize: internalPageSize, pageSizeOptions: [10, 25, 50, 100], onPageSizeChange: handlePageSizeChange, totalRecords: total, previousLabel: t('previous', 'Anterior'), nextLabel: t('next', 'Siguiente'), perPageLabel: t('perPage', 'por página'), recordsLabel: t('records', 'registros'), goToPageLabel: t('goToPage', 'Página') }} showRowActions={showRowActions} actions={actionsList} onEdit={canUpdate ? handleEdit : undefined} onDelete={canDelete ? handleDeleteClick : undefined} rowActions={rowActions} locale={locale} enableMultiSelect={enableMultiSelect} selectedIds={selectedIds} onSelectionChange={handleSelectionChange} columnFilterableFields={effectiveColumnFilterableFields} columnFilters={Object.fromEntries(Object.entries(columnFilters).map(([k, v]) => [k, v.value]))} onColumnFilterChange={handleColumnFilterChange} showCompanyStatusColumn={isCompanyScopeActive} isRecordCompanyEnabled={isRecordCompanyEnabled} onCompanyStatusClick={(row) => setLinkModalState({ show: true, record: row as T, variant: 'manual' })} t={t} />
                      </div>
                    ) : (
                      <div className="flex flex-1 min-h-0 flex-col items-center justify-center py-20 px-4 text-center">
                        {emptyStateConfig?.image ? (typeof emptyStateConfig.image === 'string' ? <img src={emptyStateConfig.image} alt="Empty" className="w-48 h-48 mb-6 opacity-50" /> : <div className="mb-6 opacity-50">{emptyStateConfig.image}</div>) : <div className="mb-6 flex items-center justify-center"><img src={noDataImage} alt="No hay registros" className="w-64 h-64 mb-4 object-contain" /></div>}
                        <div className="w-full max-w-md text-left">
                          <Info title={emptyStateConfig?.title || t('emptyTitle', 'Aún no tienes registros')} description={emptyStateConfig?.description || t('emptyDesc', 'Dale click en el botón nuevo para iniciar')} color="blue" />
                        </div>
                      </div >
                    )
                  ) : (
                    <div className="flex-1 min-h-0 overflow-auto -mx-1 px-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {data.length > 0 ? data.map((record, index) => (
                        <div key={(record as any)[idField] || index} ref={index === data.length - 1 && viewType === 'grid' && enableGridInfiniteScroll ? lastElementRef : null}>
                          <MasterCRUDCard data={record} fields={fields} actions={actionsList} onEdit={canUpdate ? handleEdit : undefined} onDelete={canDelete ? handleDeleteClick : undefined} rowActions={rowActions} index={index} renderCardContent={renderCardContent} enableMultiSelect={enableMultiSelect} isSelected={selectedIds.includes((record as any)[idField])} onSelect={(selected) => { if (selected) { setSelectedRecords(prev => [...prev, record]); } else { const id = (record as any)[idField]; setSelectedRecords(prev => prev.filter(item => (item as any)[idField] !== id)); } }} t={t} />
                        </div>
                      )) : !loading && (
                        <div className="col-span-full flex flex-col items-center justify-center py-20 px-4 text-center">
                          {emptyStateConfig?.image ? (typeof emptyStateConfig.image === 'string' ? <img src={emptyStateConfig.image} alt="Empty" className="w-48 h-48 mb-6 opacity-50" /> : <div className="mb-6 opacity-50">{emptyStateConfig.image}</div>) : <div className="mb-6 flex items-center justify-center"><img src={noDataImage} alt="No hay registros" className="w-64 h-64 mb-4 object-contain" /></div>}
                          <div className="w-full max-w-md text-left">
                            <Info title={emptyStateConfig?.title || t('emptyTitle', 'Aún no tienes registros')} description={emptyStateConfig?.description || t('emptyDesc', 'Dale click en el botón nuevo para iniciar')} color="blue" />
                          </div>
                        </div >
                      )}
                      {loading && Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-48 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />)}
                    </div >
                    </div >
                  )}
                </>
              )}
            </div >

            {/* Sidebar Interior (Desplaza al listado) */}
            {
              navigationType === 'sidebar' && (viewState === 'CREATE' || viewState === 'EDIT') && (
                <div className="w-[450px] flex-none p-5 pl-0 animate-in slide-in-from-right duration-300 flex flex-col overflow-hidden">
                  <div className="max-h-full h-fit bg-white dark:bg-dark-bg-primary rounded-2xl overflow-hidden border border-border-secondary dark:border-dark-border-primary flex flex-col shadow-sm">
                    <MasterCrudForm
                      ref={formRef}
                      fields={fields as any}
                      entityName={entityName}
                      initialData={selectedRecord || undefined}
                      isEditing={viewState === 'EDIT'}
                      dynamicFeatureCode={dynamicFeatureCode}
                      hideDynamicTabs={hideDynamicTabs}
                      loading={formLoading}
                      onSubmit={handleFormSubmit as any}
                      onCancel={handleFormCancel}
                      saveButtonText={saveButtonText || t('save')}
                      cancelButtonText={cancelButtonText || t('cancel')}
                      columns={1}
                      width="100%"
                      idField={idField as string}
                      className="max-h-full h-fit border-none rounded-none shadow-none"
                      actions={(viewState === 'EDIT' ? actionsList : undefined) as any}
                      selectedCompany={selectedCompany}
                      activeByCompany={activeByCompany}
                      companyRequired={companyRequired}
                      fullWidthButton={true}
                      showGlobalValues={showGlobalValues}
                      onSearchMatches={handleSearchMatches as any}
                      canOverrideMultiCompany={canOverrideMultiCompany}
                      companies={allCompanies}
                      onCompanyChange={handleCompanyChange}
                      onShowGlobalValuesChange={setShowGlobalValues}
                      onUseGlobalData={() => setShowGlobalResetConfirm(true)}
                      onCreateNew={handleCreate}
                      onLinkCompanies={() => setLinkModalState({ show: true, record: selectedRecord, variant: 'manual' })}
                      renderContent={renderForm as any}
                      featCodeFileManager={featCodeFileManager}
                      fileManagerConfig={effectiveFileManagerConfig}
                      formTabs={formTabs}
                      t={t}
                    />
                  </div>
                </div>
              )
            }
              </div >
            </div >
          </div >
      ) : (
        /* Formulario en Modo Página */
        <div className="flex-1 flex flex-col min-h-0 w-full animate-in fade-in duration-300 overflow-hidden" style={{ width: '100%', minWidth: '100%' }}>
          {/* Header del detalle: transparente, centrado (max-w-7xl), alineado como el listado (S2.1) */}
          <div className="flex-shrink-0">
            <div className="max-w-7xl w-full mx-auto px-4 md:px-6 pt-4 md:pt-5 pb-6 space-y-3">
              <nav>
                <button type="button" onClick={handleBackGuarded} className="inline-flex items-center gap-1 text-sm text-content-tertiary dark:text-dark-content-tertiary hover:text-[#1355b7] transition-colors">
                  <BackIcon className="w-4 h-4" /><span>{t('back', 'Volver')}</span>
                </button>
              </nav>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex flex-col gap-2 min-w-0">
                  {/* Título = solo el nombre del maestro */}
                  <h1 className="text-xl font-semibold tracking-tight text-[#1355b7] dark:text-primary-600 truncate">{finalTitle}</h1>
                  {/* CompanySelector (combobox azul) + badges Activo/Edición a su lado (S2.1b) */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {activeByCompany && allCompanies.length > 0 && <CompanySelector companies={allCompanies} selectedCompany={selectedCompany} onCompanyChange={(company) => handleCompanyChange(company, (selectedRecord as any)?.[idField])} readOnly={companyRequired} companyEnabledMap={detailCompanyEnabledMap} t={t} />}
                    {(() => {
                      const recordActive = (selectedRecord as any)?.[statusField] ?? true;
                      return (
                        <span className={`inline-flex items-center h-[18px] px-1.5 text-[10px] font-semibold leading-none rounded ${recordActive ? 'bg-[#ecfccb] text-[#4d7c0f] dark:bg-lime-900/30 dark:text-lime-400' : 'bg-background-secondary text-content-tertiary dark:bg-dark-bg-secondary dark:text-dark-content-tertiary'}`}>
                          {recordActive ? t('active', 'Activo') : t('inactive', 'Inactivo')}
                        </span>
                      );
                    })()}
                    <span className="inline-flex items-center gap-1 h-[18px] px-1.5 text-[10px] font-semibold leading-none rounded bg-[#fef9c3] text-[#a16207] dark:bg-yellow-900/20 dark:text-yellow-500">
                      <PencilIcon className="w-3 h-3" />{viewState === 'EDIT' ? t('editing', 'Edición') : t('creating', 'Creación')}
                    </span>
                    {((typeof headerBadges === 'function' ? headerBadges(selectedRecord) : headerBadges || []).length > 0) && (typeof headerBadges === 'function' ? headerBadges(selectedRecord) : headerBadges || []).map((badge, idx) => <Badge key={idx} label={badge.label} color={badge.color} className="text-[10px] px-1.5 py-0" />)}
                  </div>
                  {renderHeaderExtra && <div className="mt-1">{renderHeaderExtra(selectedRecord)}</div>}
                </div>
                {/* Acciones: Cancelar · Guardar · kebab (h-9, S2.2) */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    type="outline"
                    size="base"
                    className="!h-9 !px-3 !text-sm font-medium"
                    leftIcon={<XMarkIcon className="w-4 h-4" />}
                    onClick={handleBackGuarded}
                  >
                    {cancelButtonText || t('cancel', 'Cancelar')}
                  </Button>
                  <Button
                    type="default"
                    size="base"
                    className="!h-9 !px-3 !text-sm font-medium"
                    leftIcon={
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" /><path d="M7 3v4a1 1 0 0 0 1 1h7" /></svg>
                    }
                    disabled={formLoading}
                    onClick={() => { if (formRef.current) formRef.current.submit(); }}
                  >
                    {formLoading ? '...' : (saveButtonText || t('save'))}
                  </Button>
                  {(viewState === 'EDIT' || (viewState === 'CREATE' && activeByCompany)) && (() => {
                    const extraActions = actionsList.filter(a => a.id !== 'edit' && a.id !== 'link');
                    const isGlobal = !selectedCompany || String(selectedCompany.uuid) === '000';
                    const showMasterData = activeByCompany && viewState === 'EDIT' && !isGlobal && !companyRequired;
                    const showLinkCreate = activeByCompany && viewState === 'EDIT' && !companyRequired;
                    const menuItemCls = 'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors text-left';
                    const close = () => setDetailActionsOpen(false);
                    if (!showMasterData && !showLinkCreate && extraActions.length === 0) return null;
                    return (
                      <div className="relative" ref={detailActionsRef}>
                        <button
                          type="button"
                          aria-haspopup="menu"
                          aria-expanded={detailActionsOpen}
                          title={t('moreActions', 'Más acciones')}
                          aria-label={t('moreActions', 'Más acciones')}
                          onClick={() => setDetailActionsOpen(v => !v)}
                          className="inline-flex items-center justify-center w-9 h-9 border border-border-primary dark:border-dark-border-primary rounded-md bg-background-secondary dark:bg-dark-bg-secondary hover:bg-[#ebebec] dark:hover:bg-dark-bg-primary text-content-tertiary transition-colors"
                        >
                          <EllipsisVerticalIcon className="w-4 h-4" />
                        </button>
                        {detailActionsOpen && (
                          <div role="menu" className="absolute right-0 top-full mt-1 z-50 min-w-[260px] bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-md shadow-md py-1">
                            {showMasterData && (
                              <>
                                <button type="button" role="menuitem" className={menuItemCls} onClick={() => { close(); setShowGlobalResetConfirm(true); }}>
                                  <GlobeAltIcon className="w-4 h-4 text-[#1355b7] dark:text-primary-400 flex-shrink-0" />
                                  {t('useGlobalData', 'Usar datos maestros')}
                                </button>
                                <button type="button" role="menuitem" className={`${menuItemCls} justify-between`} onClick={() => setShowGlobalValues(!showGlobalValues)}>
                                  <span className="flex items-center gap-2.5"><GlobeAltIcon className="w-4 h-4 text-[#1355b7] dark:text-primary-400 flex-shrink-0" />{t('showGlobalValues', 'Mostrar valores maestros')}</span>
                                  <span className={`w-7 h-4 rounded-full relative inline-block flex-shrink-0 transition-colors ${showGlobalValues ? 'bg-[#1355b7]' : 'bg-border-primary dark:bg-dark-border-primary'}`}>
                                    <span className="absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all" style={{ left: showGlobalValues ? '14px' : '2px' }} />
                                  </span>
                                </button>
                              </>
                            )}
                            {showLinkCreate && (
                              <>
                                {showMasterData && <hr className="my-1 border-border-primary dark:border-dark-border-primary" />}
                                <button type="button" role="menuitem" className={menuItemCls} onClick={() => { close(); setLinkModalState({ show: true, record: selectedRecord, variant: 'manual' }); }}>
                                  <LinkIcon className="w-4 h-4 text-content-tertiary flex-shrink-0" />
                                  {t('linkCompanies', 'Habilitar compañías')}
                                </button>
                                <button type="button" role="menuitem" className={menuItemCls} onClick={() => { close(); setShowCreateConfirm(true); }}>
                                  <PlusIcon className="w-4 h-4 text-content-tertiary flex-shrink-0" />
                                  {t('createNew', 'Crear nuevo registro')}
                                </button>
                              </>
                            )}
                            {extraActions.length > 0 && (showMasterData || showLinkCreate) && <hr className="my-1 border-border-primary dark:border-dark-border-primary" />}
                            {extraActions.map(action => {
                              const actionName = typeof action.name === 'function' ? action.name(selectedRecord!) : action.name;
                              const actionIcon = typeof action.icon === 'function' ? action.icon(selectedRecord!) : action.icon;
                              const isDestructive = actionName.toLowerCase().includes('eliminar') || actionName.toLowerCase().includes('desactivar') || actionName.toLowerCase().includes('delete');
                              const disabled = typeof action.disabled === 'function' ? action.disabled(selectedRecord!) : action.disabled;
                              return (
                                <button
                                  key={action.id || actionName}
                                  type="button"
                                  role="menuitem"
                                  disabled={disabled}
                                  onClick={() => { close(); action.action?.(selectedRecord!); }}
                                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors disabled:opacity-40 ${isDestructive ? 'text-error-content hover:bg-error/10' : 'text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'}`}
                                >
                                  {actionIcon && <span className="w-4 h-4 flex-shrink-0 inline-flex items-center justify-center">{actionIcon}</span>}
                                  {actionName}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div >
          <div className={`w-full max-w-7xl mx-auto px-4 md:px-6 pb-4 ${detailConfig ? 'flex-1 min-h-0 overflow-auto py-1' : 'flex-1 min-h-0 flex flex-col'}`}>
            <div className={`w-full bg-white dark:bg-dark-bg-primary rounded-2xl border border-border-primary dark:border-dark-border-primary overflow-hidden ${detailConfig ? 'p-8 min-h-[400px] shadow-xs' : 'flex flex-col flex-1 min-h-0 shadow-sm'}`}>
              <MasterCrudForm
                key={`form-page-${viewState}-${(selectedRecord as any)?.[idField] || 'new'}`}
                ref={formRef}
                fields={fields as any}
                entityName={entityName}
                initialData={selectedRecord || undefined}
                isEditing={viewState === 'EDIT'}
                dynamicFeatureCode={dynamicFeatureCode}
                hideDynamicTabs={hideDynamicTabs}
                loading={formLoading}
                onSubmit={handleFormSubmit as any}
                onCancel={handleFormCancel}
                saveButtonText={saveButtonText || t('save')}
                cancelButtonText={cancelButtonText || t('cancel')}
                columns={formColumns}
                width="100%"
                idField={idField as string}
                className="flex-1 min-h-0"
                actions={(viewState === 'EDIT' ? actionsList : undefined) as any}
                selectedCompany={selectedCompany}
                activeByCompany={activeByCompany}
                companyRequired={companyRequired}
                fullWidthButton={false}
                showGlobalValues={showGlobalValues}
                onSearchMatches={handleSearchMatches as any}
                canOverrideMultiCompany={canOverrideMultiCompany}
                companies={allCompanies}
                onCompanyChange={handleCompanyChange}
                onShowGlobalValuesChange={setShowGlobalValues}
                onUseGlobalData={() => setShowGlobalResetConfirm(true)}
                onCreateNew={handleCreate}
                onLinkCompanies={() => setLinkModalState({ show: true, record: selectedRecord, variant: 'manual' })}
                renderContent={renderForm as any}
                featCodeFileManager={featCodeFileManager}
                fileManagerConfig={effectiveFileManagerConfig}
                showValidationErrorDialog={showValidationErrorDialog}
                formTabs={formTabs}
                t={t}
                hideHeaderFooter={true}
              />
            </div >

            {/* ─── Sección de Detalle embebida ─── */}
            {detailConfig && (
              viewState === 'EDIT' && globalRecordId != null ? (
                <MasterCrudDetailSection
                  masterId={globalRecordId}
                  config={detailConfig as MasterCrudDetailConfig<any>}
                  t={t}
                  locale={locale}
                  isInsideModal={false}
                />
              ) : viewState === 'CREATE' ? (
                /* Placeholder en CREATE: reserva el mismo espacio visual que ocupará el detalle,
                   garantizando consistencia de ancho entre CREATE y EDIT. */
                <div className="mt-6 w-full">
                  <div className="relative flex items-center mb-5">
                    <div className="flex-grow border-t border-border-primary dark:border-dark-border-primary" />
                    <span className="mx-3 text-xs font-semibold uppercase tracking-widest text-content-secondary dark:text-content-secondary select-none">
                      {(detailConfig as MasterCrudDetailConfig<any>).title || t('details', 'Detalles')}
                    </span>
                    <div className="flex-grow border-t border-border-primary dark:border-dark-border-primary" />
                  </div>
                  <div className="rounded-xl border border-dashed border-border-primary dark:border-dark-border-primary bg-background-secondary dark:bg-dark-bg-secondary/30 flex items-center justify-center py-10 px-6 text-center">
                    <p className="text-sm text-content-tertiary dark:text-dark-content-tertiary">
                      {t('detailAvailableAfterSave', 'Guarda el registro para gestionar los detalles.')}
                    </p>
                  </div>
                </div>
              ) : null
            )}
          </div >
        </div >
      )}

      {(viewState === 'CREATE' || viewState === 'EDIT') && navigationType === 'modal' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          {/* Cuando hay detailConfig en modo EDIT, expandir a pantalla casi completa para mostrar la sección de detalle */}
          <div
            className={`w-full bg-white dark:bg-dark-bg-primary rounded-xl shadow-2xl flex flex-col overflow-hidden ${
              (detailConfig && viewState === 'EDIT') || maximizeEditModal ? 'h-[95vh]' : 'max-h-[90vh]'
            }`}
            style={{ maxWidth: (detailConfig && viewState === 'EDIT') || maximizeEditModal ? '95vw' : formWidth }}
          >
            {detailConfig && viewState === 'EDIT' ? (
              /* Modo Maestro-Detalle: formulario con header/footer propio + detalle scrollable */
              <>
                <MasterCrudForm
                  ref={formRef}
                  fields={fields as any}
                  entityName={entityName}
                  initialData={selectedRecord || undefined}
                  isEditing={true}
                  dynamicFeatureCode={dynamicFeatureCode}
                  hideDynamicTabs={hideDynamicTabs}
                  loading={formLoading}
                  onSubmit={handleFormSubmit as any}
                  onCancel={handleFormCancel}
                  saveButtonText={saveButtonText || t('save')}
                  cancelButtonText={cancelButtonText || t('cancel')}
                  columns={formColumns}
                  width="100%"
                  idField={idField as string}
                  className="flex-shrink-0"
                  actions={actionsList as any}
                  selectedCompany={selectedCompany}
                  activeByCompany={activeByCompany}
                  companyRequired={companyRequired}
                  fullWidthButton={false}
                  showGlobalValues={showGlobalValues}
                  onSearchMatches={handleSearchMatches as any}
                  canOverrideMultiCompany={canOverrideMultiCompany}
                  companies={allCompanies}
                  onCompanyChange={handleCompanyChange}
                  onShowGlobalValuesChange={setShowGlobalValues}
                  onUseGlobalData={() => setShowGlobalResetConfirm(true)}
                  onCreateNew={handleCreate}
                  onLinkCompanies={() => setLinkModalState({ show: true, record: selectedRecord, variant: 'manual' })}
                  renderContent={renderForm as any}
                  featCodeFileManager={featCodeFileManager}
                  fileManagerConfig={effectiveFileManagerConfig}
                  formTabs={formTabs}
                  t={t}
                />
                {/* Sección de detalles con scroll propio, separada visualmente del formulario */}
                {globalRecordId != null && (
                  <div className="flex-1 overflow-auto px-6 pb-6">
                    <MasterCrudDetailSection
                      masterId={globalRecordId}
                      config={detailConfig as MasterCrudDetailConfig<any>}
                      t={t}
                      locale={locale}
                      isInsideModal={true}
                    />
                  </div>
                )}
              </>
            ) : (
              /* Modo normal sin detailConfig */
              <MasterCrudForm
                ref={formRef}
                fields={fields as any}
                entityName={entityName}
                initialData={selectedRecord || undefined}
                isEditing={viewState === 'EDIT'}
                dynamicFeatureCode={dynamicFeatureCode}
                hideDynamicTabs={hideDynamicTabs}
                loading={formLoading}
                onSubmit={handleFormSubmit as any}
                onCancel={handleFormCancel}
                saveButtonText={saveButtonText || t('save')}
                cancelButtonText={cancelButtonText || t('cancel')}
                columns={formColumns}
                width="100%"
                idField={idField as string}
                className="flex-1 min-h-0"
                actions={(viewState === 'EDIT' ? actionsList : undefined) as any}
                selectedCompany={selectedCompany}
                activeByCompany={activeByCompany}
                companyRequired={companyRequired}
                fullWidthButton={false}
                showGlobalValues={showGlobalValues}
                onSearchMatches={handleSearchMatches as any}
                canOverrideMultiCompany={canOverrideMultiCompany}
                companies={allCompanies}
                onCompanyChange={handleCompanyChange}
                onShowGlobalValuesChange={setShowGlobalValues}
                onUseGlobalData={() => setShowGlobalResetConfirm(true)}
                onCreateNew={handleCreate}
                onLinkCompanies={() => setLinkModalState({ show: true, record: selectedRecord, variant: 'manual' })}
                renderContent={renderForm as any}
                featCodeFileManager={featCodeFileManager}
                fileManagerConfig={effectiveFileManagerConfig}
                formTabs={formTabs}
                t={t}
              />
            )}
          </div >
        </div >
      )}

      <AlertDialog
        isOpen={showGlobalResetConfirm}
        type="warning"
        title={t('confirmGlobalResetTitle', '¿Restaurar a los valores maestros?')}
        description={t('confirmGlobalResetDesc', 'Se descartarán los cambios locales de esta compañía y los campos volverán a tomar los valores del registro maestro (Global). Esta acción no afecta a otras compañías.')}
        onCancel={() => setShowGlobalResetConfirm(false)}
        onConfirm={handleConfirmGlobalReset}
        cancelText={t('cancelText', 'Cancelar')}
        confirmText={t('confirmRestore', 'Restaurar valores')}
      />

      <AlertDialog
        isOpen={showCreateConfirm}
        type="warning"
        title={t('confirmCreateTitle', '¿Crear un nuevo registro?')}
        description={t('confirmCreateDesc', 'Se descartarán los cambios no guardados del registro actual y se abrirá un formulario en blanco para crear uno nuevo.')}
        onCancel={() => setShowCreateConfirm(false)}
        onConfirm={() => { setShowCreateConfirm(false); handleCreate(); }}
        cancelText={t('cancelText', 'Cancelar')}
        confirmText={t('confirmCreateBtn', 'Crear nuevo')}
      />

      <AlertDialog
        isOpen={linkConfirmState.show && !!linkConfirmState.company}
        type="info"
        title={t('linkConfirmTitle', 'Registro no disponible en esta compañía')}
        onCancel={() => setLinkConfirmState({ show: false, company: null })}
        onConfirm={handleConfirmDynamicLink}
        cancelText={t('cancelText', 'Cancelar')}
        confirmText={t('confirmLink', 'Habilitar y continuar')}
        description={
          <div className="flex flex-col gap-2">
            <p className="text-sm leading-5 text-content-secondary dark:text-dark-content-secondary">
              {t('linkConfirmLine1', 'Para editar este registro en')}
              {' '}
              <span className="font-semibold text-content-primary dark:text-dark-content-primary">
                {linkConfirmState.company?.name}
              </span>
              {t('linkConfirmLine2', ', primero debe estar habilitado en esta compañía.')}
            </p>
            <p className="text-sm leading-5 text-content-tertiary dark:text-dark-content-tertiary">
              {t('linkConfirmLine3', 'Se creará una sobrescritura a partir de los valores del registro maestro (Global). ¿Deseas habilitarlo ahora y continuar?')}
            </p>
          </div>
        }
      />

      {deleteConfirm.show && (() => {
        const isActive = !!((deleteConfirm.record as any)[statusField] ?? true);
        const isBranchContext = activeByCompany && !companyRequired && selectedCompany && String(selectedCompany.uuid) !== '000';

        let title = isActive ? t('confirmDeleteTitle') : t('confirmActivateTitle');
        let description = isActive ? t('confirmDeleteDesc') : t('confirmActivateDesc');
        let confirmText = isActive ? t('confirmDeleteText') : t('activate');

        if (softDelete) {
          // El botón de estado siempre usa terminología de Desactivar/Activar (nunca 'Deshabilitar').
          // En Sucursal, la descripción aclara que el alcance es la compañía seleccionada.
          if (isBranchContext) {
            title = isActive ? t('confirmDeactivateTitle', '¿Estás seguro de desactivar este registro?') : t('confirmActivateTitle', '¿Estás seguro de activar este registro?');
            description = isActive ? t('confirmDeactivateCompanyDesc', 'El registro dejará de estar disponible en esta compañía.') : t('confirmActivateCompanyDesc', 'El registro volverá a estar disponible en esta compañía.');
            confirmText = isActive ? t('deactivate', 'Desactivar') : t('activate', 'Activar');
          } else {
            // Contexto Global con softDelete: Usar terminología de Desactivar
            title = isActive ? t('confirmDeactivateTitle', '¿Estás seguro de desactivar este registro?') : t('confirmActivateTitle');
            description = isActive ? t('confirmDeactivateDesc', 'El registro dejará de estar disponible.') : t('confirmActivateDesc');
            confirmText = isActive ? t('deactivate', 'Desactivar') : t('activate', 'Activar');
          }
        }

        return (
          <AlertDialog
            isOpen
            type={isActive ? 'danger' : 'success'}
            title={title}
            description={description}
            onCancel={() => setDeleteConfirm({ show: false, record: null })}
            onConfirm={handleDeleteConfirm}
            cancelText={t('cancelText')}
            confirmText={confirmText}
          />
        );
      })()}

      {activeByCompany && linkModalState.show && linkModalState.record && (
        <LinkModal
          isOpen={linkModalState.show}
          onClose={() => {
            const wasSuccess = linkModalState.variant === 'success';
            setLinkModalState(prev => ({ ...prev, show: false, record: null }));
            if (wasSuccess) { setViewState('LIST'); setSelectedRecord(null); setGlobalRecordId(null); }
          }}
          recordId={(linkModalState.record as any)[idField]}
          record={linkModalState.record}
          companies={allCompanies}
          fields={fields}
          variant={linkModalState.variant}
          onConfirm={handleLinkRecords}
          isLinked={(companyUuid) => checkLinkStatus((linkModalState.record as any)[idField], companyUuid)}
          t={t}
        />
      )}

      {matchModalState.show && (
        <MatchModal isOpen={matchModalState.show} onClose={() => setMatchModalState(prev => ({ ...prev, show: false }))} matches={matchModalState.matches} fieldName={matchModalState.fieldName} onView={handleViewMatch} onContinue={() => setMatchModalState(prev => ({ ...prev, show: false }))} />
      )}

      {/* Modal de cambios sin guardar (dirty state) al Volver (S2.8) */}
      {dirtyModal.show && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDirtyModal({ show: false, changes: [] })} />
          <div className="relative w-full max-w-[560px] bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-2xl overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#fef9c3] dark:bg-yellow-900/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#a16207]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-bold text-content-primary dark:text-dark-content-primary">{t('unsavedTitle', 'Tienes cambios sin guardar')}</h3>
                  <p className="text-sm text-content-tertiary dark:text-dark-content-tertiary mt-1 leading-5">{t('unsavedDesc', 'Si sales ahora perderás los siguientes cambios. ¿Deseas guardarlos antes de volver al listado?')}</p>
                  <div className="mt-3 rounded-lg border border-border-primary dark:border-dark-border-primary overflow-hidden">
                    <p className="px-3 py-1.5 bg-background-secondary dark:bg-dark-bg-secondary text-[11px] font-semibold uppercase tracking-[0.3px] text-content-secondary border-b border-border-primary dark:border-dark-border-primary">{t('modifiedFields', 'Campos modificados')}</p>
                    <ul className="max-h-[180px] overflow-y-auto divide-y divide-border-secondary dark:divide-dark-border-primary">
                      {dirtyModal.changes.map((c, i) => (
                        <li key={i} className="px-3 py-2 text-xs">
                          <p className="font-semibold text-content-primary dark:text-dark-content-primary">{c.label}</p>
                          <p className="mt-0.5 text-content-tertiary flex items-center gap-1.5 flex-wrap">
                            {String(c.from) === '' ? <span className="italic text-content-secondary">({t('emptyValue', 'vacío')})</span> : <span className="font-medium text-content-primary dark:text-dark-content-primary">{String(c.from)}</span>}
                            <svg className="w-3 h-3 text-content-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                            {String(c.to) === '' ? <span className="italic text-content-secondary">({t('emptyValue', 'vacío')})</span> : <span className="font-medium text-content-primary dark:text-dark-content-primary">{String(c.to)}</span>}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-primary dark:border-dark-border-primary">
              <Button type="plain" size="base" className="!h-9 !px-3 !text-sm font-medium whitespace-nowrap" onClick={() => setDirtyModal({ show: false, changes: [] })}>{t('keepEditing', 'Seguir editando')}</Button>
              <Button type="outline" size="base" className="!h-9 !px-3 !text-sm font-medium whitespace-nowrap !text-error-content !border-error-content" onClick={() => { setDirtyModal({ show: false, changes: [] }); handleFormCancel(); }}>{t('discardExit', 'Descartar y salir')}</Button>
              <Button type="default" size="base" className="!h-9 !px-3 !text-sm font-medium whitespace-nowrap" onClick={() => { setDirtyModal({ show: false, changes: [] }); if (formRef.current) formRef.current.submit(); }}>{t('saveExit', 'Guardar y salir')}</Button>
            </div>
          </div>
        </div>
      )}

      {previewModalState.show && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-dark-bg-primary rounded-xl shadow-2xl flex flex-col w-[95vw] h-[95vh] overflow-hidden relative">
            <div className="flex items-center justify-between p-4 border-b border-border-secondary dark:border-dark-border-primary shrink-0">
              <div className="flex items-center gap-4"><h3 className="text-lg font-bold text-content-primary dark:text-dark-content-primary">{t('preview', 'Vista Previa')}</h3></div>
              <Button type="outline" size="xs" iconOnly leftIcon={<XMarkIcon className="w-4 h-4" />} onClick={() => setPreviewModalState(prev => ({ ...prev, show: false }))} ariaLabel={t('close', 'Cerrar')} className="!bg-white dark:!bg-dark-bg-primary" />
            </div>
            <div className="flex-1 overflow-auto bg-background-secondary dark:bg-dark-bg-primary p-5">
              <div className="bg-white dark:bg-dark-bg-primary rounded-2xl p-8 w-full min-h-[400px] shadow-xs">
                {previewModalState.loading ? (
                  <div className="flex items-center justify-center h-full py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-custom-600"></div>
                  </div>
                ) : previewModalState.record && (
                  renderForm ? renderForm({
                    fields, entityName, initialData: previewModalState.record,
                    isEditing: true, loading: false, isReadOnly: true,
                    onSubmit: () => { }, onCancel: () => { },
                    columns: formColumns, width: "100%", className: "w-full",
                    hideHeaderFooter: true,
                    selectedCompany: globalCompany,
                    activeByCompany,
                    canOverrideMultiCompany,
                    t
                  }) :
                    <MasterCrudForm
                      fields={fields as any}
                      entityName={entityName}
                      initialData={previewModalState.record}
                      isEditing={true}
                      loading={false}
                      isReadOnly={true}
                      dynamicFeatureCode={dynamicFeatureCode}
                      hideDynamicTabs={hideDynamicTabs}
                      onSubmit={() => { }}
                      onCancel={() => { }}
                      columns={formColumns}
                      width="100%"
                      className="w-full"
                      hideHeaderFooter={true}
                      selectedCompany={globalCompany}
                      activeByCompany={activeByCompany}
                      companyRequired={companyRequired}
                      canOverrideMultiCompany={canOverrideMultiCompany}
                      t={t}
                    />
                )}
              </div>
            </div>
            <div className="px-[24px] py-[16px] pb-[24px] flex items-center justify-end gap-[12px] shrink-0 w-full border-t border-border-secondary dark:border-dark-border-primary bg-white dark:bg-dark-bg-primary">
              <Button type="outline" size="l" onClick={() => setPreviewModalState(prev => ({ ...prev, show: false }))} className="font-medium px-6">{t('cancelText', 'Cancelar')}</Button>
              <Button type="default" size="l" onClick={() => { const record = previewModalState.record; setPreviewModalState({ show: false, record: null, loading: false }); setMatchModalState(prev => ({ ...prev, show: false })); if (record) handleEdit(record); }} className="font-bold px-8">{t('edit', 'Editar')}</Button>
            </div>
          </div>
        </div>
      )}

      {showBulkDynamicEditor && dynamicFeatureCode && selectedRecords.length > 0 && (
        <MasterCrudBulkDynamicEditor
          featureCode={dynamicFeatureCode}
          recordIds={selectedRecords.map(r => String((r as any)[idField]))}
          onClose={() => setShowBulkDynamicEditor(false)}
          onComplete={handleBulkUpdateComplete}
          t={t}
        />
      )}

      {customModalState.show && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className={`bg-white dark:bg-dark-bg-primary rounded-xl shadow-2xl flex flex-col relative ${maximizeCustomModal ? 'w-[95vw] h-[95vh]' : 'w-full max-w-4xl max-h-[90vh]'}`}>
            <div className="flex items-center justify-between p-4 border-b border-border-secondary dark:border-dark-border-primary">
              <div className="flex items-center gap-4">
                <Button type="plain" size="sm" iconOnly leftIcon={<BackIcon className="w-5 h-5" />} onClick={handleCloseCustomModal} ariaLabel={t('back', 'Volver')} />
                <h3 className="text-lg font-bold text-content-primary dark:text-dark-content-primary">{customModalState.title}</h3>
              </div>
              <Button type="outline" size="xs" iconOnly leftIcon={<XMarkIcon className="w-4 h-4" />} onClick={handleCloseCustomModal} ariaLabel={t('close', 'Cerrar')} className="!bg-white dark:!bg-dark-bg-primary" />
            </div>
            <div className="flex-1 overflow-auto p-4">{customModalState.content}</div>
          </div>
        </div>
      )}

      {auditModalState.show && auditHistory && auditModalState.record && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-dark-bg-primary rounded-xl shadow-2xl flex flex-col relative w-full max-w-3xl max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-border-secondary dark:border-dark-border-primary">
              <h3 className="text-lg font-bold text-content-primary dark:text-dark-content-primary">
                {t('auditHistory', 'Historial de cambios')}
              </h3>
              <Button type="outline" size="xs" iconOnly leftIcon={<XMarkIcon className="w-4 h-4" />}
                onClick={() => setAuditModalState({ show: false, record: null })}
                ariaLabel={t('close', 'Cerrar')} className="!bg-white dark:!bg-dark-bg-primary" />
            </div>
            <div className="flex-1 overflow-hidden">
              <AuditHistoryPanel
                fetchFn={auditHistory.fetchFn}
                entityId={String((auditModalState.record as any).id ?? (auditModalState.record as any).uuid ?? '')}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
